import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { formStateChange } from '@/redux/slices/formSlice';
import Accordion from './components/Accordion';
import Header from './components/Header';
import ReviewSlider from './components/ReviewSlider';
import { Button, convertDollarToCents, Loader } from '@thdinc/hpf-shared';
import SelectSearch from 'react-select-search';
import DatePicker from 'react-datepicker';
import 'react-select-search/style.css';
import {
    EventTypes,
    GuestRanges,
    EventLocation,
    TypeOfServiceOptions,
    TimeOptions,
    Contracts,
    CocktailProviders,
    getGuestRangeFromValue,
    EVENT_TYPE_MAP,
} from './constants/Options';
import { ACCORDION_TITLES } from './constants/AccordionTitles';
import { PlacesData } from '../company/interface';
import PlacesInput from '@/components/input/PlacesInput';
import { API_ROUTES } from '@/services/environment';
import { useAppSelector } from '@/hooks/hook';
import { GET, POST, PUT } from '@/services/api';
import { type Drinker } from './components/Drinker';
import QuotePreview from './components/QuotePreview';
import BartenderPreview from './components/BartenderPreview';
import { type QuoteItem } from './components/BartenderPackageItem';
import CocktailItem, { CocktailItemProps } from './components/CocktailItem';
import MaxAlcoholCard from './components/MaxAlcoholCard';
import CocktailPreview from './components/CocktailPreview';
import SearchSelect from '@/components/StyledSelect/SearchSelect';
import AddtionalPreview from './components/AddtionalInfoPreview';
import PaymentPreview from './components/PaymentPreview';
import ToggleInput from '@/components/toggle/Toggle';
import CostSummaryCard from './components/CostSummaryCard';
import PackageDetailsCard from './components/PackageDetailsCard';
import calenderIcon from '@/assets/icons/calendar_icon.svg';
import infoIcon from '@/assets/images/info.png';
import downloadIcon from '@/assets/images/download.svg';
import shareIcon from '@/assets/images/share.svg';
import { useDownloadPdf } from '@/hooks/usePdfDownload';
// 12h time helpers you said exist in your project
import {
    diffMinutesSameDay,
    diffMinutesAllowNextDay,
    getValidEndOptions,
    getEndTimeMinutesAfter,
    isMoreThan7DaysAway,
    combineEventDateAndTime,
    combineEventDateAndTimeLocal,
    isEndTimeNextDay,
    splitEventDateAndTime,
    splitEventDateAndTimeInZone,
    mapBartenderPackagesForFrontend,
    payloadDrinkType,
    dollarsToCents,
    centsToDollors,
    formattedDate,
    twoDaysBeforeEventDate,
} from '@/helpers/TimeFormat';
import {
    calculateDiscountedTotal,
    calculateEstimatedDrinks,
    calculatePackagePlatformFee,
    calculatePlatformFee,
    cocktailPrice,
    fmt,
    getCocktailPricePerItemCents,
    getCocktailProduct,
    getProductQuantity,
    isBeerProduct,
    isSparklingWineProduct,
    isWaterProduct,
    isWineProduct,
} from '@/helpers/quoteFunctions';
import Alert from '@/components/alert/Alert';
import ChangesAlert from '@/components/alert/ChangesAlert';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SaveTemplateModal from './models/SaveTemplateModal';
import GeneratedLinkModal from './models/GeneratedLinkModal';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import type { ItemLine, CocktailItemLine, QuoteForm } from './types/quoteBuilder.types';
import { Package, PackageType } from '@/interfaces/Package';
import {
    calculateEstimatedTotalsFromPackage,
    getMileageCharge,
    getStraightLineMiles,
    GeoPoint,
} from '../company/PriceCalculation2/utils/priceCalculation.utils';
import { FREE_MILES } from '../company/PriceCalculation2/constants/priceCalculation.constants';

const {
    getRevnuePackage,
    getStoreByBusinessId,
    getAllTypes,
    getCocktailsByType,
    getProductBrand,
    createQuote,
    createQuoteCheckout,
    getAllContracts,
    productsPreferences,
    getCompanyApi,
    listAllQuotesWithFilteration,
} = API_ROUTES;
const MIN_EVENT_GAP_MINUTES = 30; // minimum minutes between start and end

const nnum = (v: unknown): number => {
    const n = typeof v === 'string' && v.trim() === '' ? NaN : Number(v);
    return Number.isFinite(n) ? n : 0;
};

const shiftCalendarDate = (date: Date, days: number): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

const calendarDateToISODateUTC = (date: Date | null | undefined): string | null => {
    if (!date) return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
};


const QuoteBuilder = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { id: routeQuoteId } = useParams<{ id?: string }>();
    const paramsQuote = location.state?.quote;
    // Falls back to the route param so a direct link (e.g. /quoteBuilder/:id) opens
    // that quote even when it wasn't reached via navigate() with location.state.
    const quoteIdToLoad = paramsQuote?.id || routeQuoteId;
    // Edit links only pass { currBusiness } in location.state, never the quote itself,
    // so is_template is unknown until the quote is fetched below.
    const [loadedIsTemplate, setLoadedIsTemplate] = useState<boolean>(false);
    const isTemplateQuote = paramsQuote?.id ? !!paramsQuote?.is_template : loadedIsTemplate;
    const [paramscurrBusiness, setParamsCurrBusiness] = useState<any>(location.state?.currBusiness);
    // accordion + slider
    const [openIndex, setOpenIndex] = useState<number | null>(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [previewVisible, setPreviewVisible] = useState<boolean>(false);
    const [typeLoading, setTypeLoading] = useState<boolean>(false);
    const [brandLoading, setBrandLoading] = useState<boolean>(false);
    const [quoteLoading, setQuoteLoading] = useState<boolean>(false);
    const [isCardModalOpen, setIsCardModalOpen] = useState<boolean>(false);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState<boolean>(false);
    const [isLinkLoading, setIsLinkLoading] = useState<boolean>(false);
    const [showLeaveModal, setShowLeaveModal] = useState<boolean>(false);
    const [hasUserInteractedWithForm, setHasUserInteractedWithForm] = useState(false);
    const [checkoutLink, setCheckoutLink] = useState<string>('');
    const [linkCopied, setLinkCopied] = useState(false);
    const { refreshToken } = useAppSelector((store) => store.user);
    const { downloadQuotePdf, loading: downloadLoading } = useDownloadPdf();
    const [templateName, setTemplateName] = useState('');
    const [submitAction, setSubmitAction] = useState<'draft' | 'template' | 'updateTemplate'>('draft');
    const [templateOptions, setTemplateOptions] = useState<any[]>([]);
    const [templateOptionsLoading, setTemplateOptionsLoading] = useState<boolean>(false);
    const [selectedPackageTemplateId, setSelectedPackageTemplateId] = useState<string>('');
    const [applyingPackageTemplate, setApplyingPackageTemplate] = useState<boolean>(false);
    // other states not part of the Event Details form
    const [currentContract, setCurrentContract] = useState<any>();
    const [notes, setNotes] = useState('');
    const [place, setPlace] = useState<PlacesData | null>(null);
    //cocktail packages list
    const [packageList, setPackageList] = useState<any[]>([]);
    // Pricing Calculator sync: package used to estimate Total/Profit for this quote
    const [calcPackageId, setCalcPackageId] = useState<string>('');
    const [includePortableBar, setIncludePortableBar] = useState<boolean>(true);
    const [includeIceAndCooler, setIncludeIceAndCooler] = useState<boolean>(true);
    const [includeTravelCost, setIncludeTravelCost] = useState<boolean>(true);
    // Off by default, same as the Pricing Calculator's Gratuity toggle
    const [includeGratuity, setIncludeGratuity] = useState<boolean>(false);
    const [businessGeo, setBusinessGeo] = useState<GeoPoint | null>(null);
    const [storeList, setStoreList] = useState<any[]>([]);
    const [packageCocktailType, setPackageCocktailType] = useState<any[]>([]);
    const [packageCocktails, setPackageCocktails] = useState<any[]>([]);
    const [storeBrand, setStoreBrand] = useState<any[]>([]);
    const [preferredProducts, setPreferredProducts] = useState<any[]>([]);
    const [quote, setQuote] = useState<any>();
    const [contracts, setContracts] = useState<any[]>([]);
    // Drinking pace picker is hidden and fixed to 'light', so for a brand new quote there's no
    // click to wait for — treat it as already "chosen" so cocktail qty auto-calc runs from the
    // start. Existing quotes/templates still reset this to false on load (see fetchAndReset)
    // so their saved item quantities aren't overwritten before the user interacts.
    const drinkerClickedByUser = useRef(!quoteIdToLoad);
    // tracks previous cocktail list length to detect user-driven add/remove (vs. initial load)
    const prevCocktailLength = useRef(0);
    const platformFee = 1.05;
    const alcoholPlatformFee = 0;
    const stateTaxFee = 10;
    // -------------------- RHF setup (First Accordion only) --------------------
    const {
        control,
        register,
        watch,
        setValue,
        handleSubmit,
        reset,
        getValues,
        trigger,
        formState: { errors, isDirty },
    } = useForm<QuoteForm>({
        defaultValues: {
            currency: 'USD',
            event: {
                eventAddress: '',
                noOfGuests: '',
                eventDate: null,
                startTime: '',
                endTime: '',
                drinker: 'light',
                estDrinks: '',
                eventType: '',
                eventLocation: '',
                typeOfService: '',
                additionalInfo: '',
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
            },
            items: [],
            draft: {
                id: '',
                provider: '',
                name: '',
                qty: '',
                costPerItem: '',
                profitPerItem: '',
                pricePerItem: '',
                lineTotal: '',
                editingIndex: null,
            },
            cocktailItem: [],
            cocktail: {
                id: '',
                provider: null as any, // ← not ""
                qty: '',
                type: null as any,
                cocktails: null as any,
                brand: null as any,
                brandName: null as any,
                pricePerItem: '',
                editingIndex: null,
            },
            discount: { enabled: false, name: '', amount: '', percentage: '' },
            payment: {
                fullPayment: false,
                deposit: '',
                platformFee: '',
                totalDeposit: '',
                firstPaymentDueDate: null,
                secondPayment: '',
                secondPaymentDueDate: null,
            },
        },
        mode: 'onChange',
    });
    // Sync unsaved state to Redux only when user has interacted, so empty screen never blocks navigation
    useEffect(() => {
        const shouldBlock = hasUserInteractedWithForm && isDirty;
        dispatch(formStateChange(shouldBlock));
        return () => {
            dispatch(formStateChange(false));
        };
    }, [isDirty, hasUserInteractedWithForm, dispatch]);
    useEffect(() => {
        const fetchAndReset = async () => {
            if (!quoteIdToLoad) return;
            drinkerClickedByUser.current = false;
            const [res, allTypes] = await Promise.all([
                GET(`${createQuote}/${quoteIdToLoad}`, refreshToken),
                getAllSystemTypes(),
            ]);
            const data = res?.data?.data;
            // Direct link case: no business came in via location.state, so derive it
            // from the loaded quote's business_id.
            if (!paramscurrBusiness?.id && data?.business_id) {
                try {
                    const businessRes = await GET(`${getCompanyApi}/${data.business_id}`, refreshToken);
                    if (businessRes?.data) {
                        setParamsCurrBusiness(businessRes.data);
                    }
                } catch (error) {
                    console.log('get error fetching business for quote---:', error);
                }
            }
            //discount calculation logic
            const isPercentage = data?.is_discount_percentage;
            const discountTotal = centsToDollors(data?.discount_total ?? 0);
            const packageTotal = centsToDollors(data?.bartender_package_total ?? 0);
            const selectedContract = res?.data?.quoteContract;
            // Precompute estDrinks so existing quotes restore estimated drinks without re-clicking Heavy/Medium/Light
            let precomputedEstDrinks: number | '' = '';
            try {
                const guests = Number(data?.guests) || 0;
                const startTimeStr = data?.start_time
                    ? splitEventDateAndTime(data.start_time).formattedTime
                    : '';
                const endTimeStr = data?.end_time
                    ? splitEventDateAndTime(data.end_time).formattedTime
                    : '';
                const drinkerType = data?.drinker_type as Drinker | undefined;
                if (guests > 0 && startTimeStr && endTimeStr && drinkerType) {
                    const eventLengthHours = Math.ceil(
                        diffMinutesAllowNextDay(startTimeStr, endTimeStr) / 60,
                    );
                    precomputedEstDrinks = calculateEstimatedDrinks(guests, drinkerType, eventLengthHours)
                }
            } catch {
                precomputedEstDrinks = '';
            }
            //set local states
            if (selectedContract) {
                const formattedContracts = {
                    ...selectedContract,
                    name: selectedContract?.title, // ✅ only keep name
                };
                setCurrentContract(formattedContracts);
            }
            setCheckoutLink(res?.data?.client_facing_link || '');
            setQuote(data?.id); // optional
            setLoadedIsTemplate(!!data?.is_template);
            setNotes(data?.notes || '');
            setTemplateName(data?.template_title || '');
            setPlace({
                address: data?.address1,
                lat: data?.location?.coordinates[1] ?? 0,
                lng: data?.location?.coordinates[0] ?? 0,
                city: data?.city,
                state: data?.state,
                zip: data?.postal_code,
                timezone: data?.timezone,
            });
            // Use browser timezone so user sees the time as they expect (e.g. 2 PM–3 PM in their zone)
            const startInZone = data?.start_time
                ? splitEventDateAndTimeInZone(data.start_time)
                : { eventDate: null, formattedTime: '' };
            const endInZone = data?.end_time
                ? splitEventDateAndTimeInZone(data.end_time)
                : { eventDate: null, formattedTime: '' };
            reset({
                currency: 'USD',
                event: {
                    eventAddress: data.address1 || '',
                    noOfGuests: data.guests || '',
                    eventDate: startInZone.eventDate,
                    startTime: startInZone.formattedTime,
                    endTime: endInZone.formattedTime,
                    drinker: data.drinker_type || 'light',
                    estDrinks: precomputedEstDrinks,
                    eventType: data.event_type || '',
                    eventLocation: data.event_site || '',
                    typeOfService: data.type_of_service || '',
                    additionalInfo: data.additional_info || '',
                    firstName: data.first_name || '',
                    lastName: data.last_name || '',
                    email: data.email || '',
                    phone: data.phone || '',
                },
                draft: {
                    id: '',
                    provider: '',
                    name: '',
                    qty: '',
                    costPerItem: '',
                    profitPerItem: '',
                    pricePerItem: '',
                    lineTotal: '',
                    editingIndex: null,
                },
                items: mapBartenderPackagesForFrontend(data.quote_bartender_package || []),
                cocktail: {
                    id: '',
                    provider: null as any, // ← not ""
                    qty: '',
                    type: null as any,
                    cocktails: null as any,
                    brand: null as any,
                    brandName: null as any,
                    pricePerItem: '',
                    editingIndex: null,
                },
                cocktailItem: await buildCocktailItemsFromQuote(data, allTypes),
                payment: {
                    deposit: centsToDollors(data?.deposit ?? 0) || '',
                    totalDeposit: centsToDollors(data?.total_deposit ?? 0) || '',
                    platformFee: centsToDollors(data?.platform_fee ?? 0) || '',
                    firstPaymentDueDate: splitEventDateAndTime(data?.first_payment_due_date).eventDate,
                    secondPaymentDueDate: splitEventDateAndTime(
                        data?.second_payment_due_date,
                    ).eventDate,
                    fullPayment: data?.is_full_payment
                },
                discount: {
                    name: data?.discount_name,
                    enabled: data?.discount_applied,
                    percentage: isPercentage ? (discountTotal / packageTotal) * 100 : 0,
                    amount: isPercentage ? 0 : discountTotal,
                }
            });
        };

        fetchAndReset();
    }, [quoteIdToLoad]);
    //cocktail watches
    const cocktailSelectedType = watch('cocktail.type');
    const cocktailSelected = watch('cocktail.cocktails');
    const storeSelected = watch('cocktail.provider');
    const paymentDeposit = watch('payment.deposit');

    useEffect(() => {
        const getStore = async () => {
            try {
                const res = await GET(`${getStoreByBusinessId}/${paramscurrBusiness?.id}`, refreshToken);
                if (res.data) {
                    if (res.data?.data?.length > 0) {
                        setStoreList([...res.data.data, ...CocktailProviders]);
                    }
                }
            } catch (error) {
                console.log('get error in storelist---:', error);
            }
        };
        const getProductAllTypes = async () => {
            try {
                setTypeLoading(true);
                const res = await GET(`${getAllTypes}`, refreshToken);
                if (res.data) {
                    if (res.data?.data?.length > 0) {
                        const cocktails =
                            res.data.data.find((x: any) => x.category === 'cocktails')?.types || [];
                        const products =
                            res.data.data.find((x: any) => x.category === 'products')?.types || [];
                        // ---------- Step 2: remove duplicates from products and beer_cocktail ----------
                        const filteredProducts = products.filter(
                            (item: any) =>
                                !cocktails.includes(item) &&
                                item !== 'beer_cocktail' &&
                                item !== 'mixers',
                        ); // ---------- Step 3: helper to capitalize ----------
                        const capitalize = (str: string) => {
                            return str
                                .replace(/_/g, ' ')
                                .split(' ')
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ');
                        };
                        // ---------- Step 4: format into new structure ----------
                        const formatted = [
                            ...cocktails.map((item: any, i: number) => ({
                                id: `cocktail-${Date.now()}-${i}`,
                                type: 'cocktails',
                                name: capitalize(item),
                                value: item,
                            })),
                            ...filteredProducts.map((item: any, i: number) => ({
                                id: `product-${Date.now()}-${i}`,
                                type: 'products',
                                name: capitalize(item),
                                value: item,
                            })),
                        ];
                        const others = formatted.filter((item) => item.value === 'other');
                        const withoutOthers = formatted.filter((item) => item.value !== 'other');
                        setPackageCocktailType([...withoutOthers]);
                    }
                }
            } catch (error) {
                setTypeLoading(false);
                console.log('get error in storelist---:', error);
            }
        };
        const getPackages = async () => {
            try {
                const res = await GET(
                    `${getRevnuePackage}/?business_id=${paramscurrBusiness?.id}`,
                    refreshToken,
                );
                if (res.data) {
                    if (res.data?.length > 0) {
                        setPackageList([
                            ...res.data,
                            { name: 'Customer Provide', value: 'other', id: 'other' },
                        ]);
                    }
                }
            } catch (error) {
                console.log('get error in packages---:', error);
            }
        };
        getStore();
        getProductAllTypes();
        getPackages();
    }, [paramscurrBusiness?.id]);
    useEffect(() => {
        if (!paramscurrBusiness?.id) return;
        (async () => {
            try {
                const res = await GET(`${productsPreferences}/by-business/${paramscurrBusiness.id}`, refreshToken);
                if (res?.data?.length > 0) {
                    setPreferredProducts(res.data);
                }
            } catch (error) {
                Alert({ type: 'error', message: 'Unable to fetch product preferences' });
            }
        })();
    }, [paramscurrBusiness?.id]);
    useEffect(() => {
        if (cocktailSelectedType?.type) {
            getPackageCocktails();
        }
    }, [cocktailSelectedType]);
    useEffect(() => {
        (async () => {
            if (cocktailSelectedType?.type !== 'cocktails') {
                getProductBrandApi();
            }
            if (cocktailSelectedType?.type == 'cocktails') {
                // Make sure cocktail_ingredients is always an array
                const ingredients = Array.isArray(cocktailSelected?.cocktail_ingredients)
                    ? cocktailSelected.cocktail_ingredients
                    : [];
                const products =
                    ingredients
                        .filter(
                            (ing: any) => ing?.product && ing?.product?.type !== 'mixers', // exclude mixers
                        )
                        .map((ing: any) => ing) ?? [];
                if (cocktailSelected?.value === 'custom') {
                    getProductBrandApi(cocktailSelectedType?.value)

                }
                // ✅ Now apply the cocktailSelectedType filter logic
                // if (cocktailSelectedType?.value) {
                //     const filtered = products.filter(
                //         (p: any) => p?.type === cocktailSelectedType?.value,
                //     );
                //     if (filtered.length > 0) {
                //         products = filtered;
                //     }
                // If a match exists, use those; else fallback to the first product
                // products = filtered.length > 0 ? filtered : products.slice(0, 1);
                // } else {
                //     // No selected type, default to first product
                //     products = products.slice(0, 1);
                // }
                // }

                // ✅ Add "Other" option
                setStoreBrand(sortPreferredFirst(products));
            }
        })()
    }, [cocktailSelectedType, storeSelected, cocktailSelected]);
    // -----------------------------
    // Fetch Contracts
    // -----------------------------
    const fetchContracts = async () => {
        try {
            const res = await GET(
                `${getAllContracts}?businessId=${paramscurrBusiness?.id}`,
                refreshToken
            );

            const formattedContracts =
                res?.data?.rows?.map(({ title, ...rest }: any) => ({
                    ...rest,
                    name: title, // ✅ only keep name
                })) || [];

            setContracts(formattedContracts);
        } catch (err) {
            console.log(err, "Failed to load contracts");
        }
    };

    useEffect(() => {
        fetchContracts();
    }, [paramscurrBusiness?.id]);
    // -----------------------------
    // Fetch Quote Templates (for "Select Template" prefill)
    // -----------------------------
    useEffect(() => {
        if (!paramscurrBusiness?.id) return;
        setTemplateOptionsLoading(true);
        (async () => {
            try {
                const res = await GET(
                    `${listAllQuotesWithFilteration}?is_template=true&business_id=${paramscurrBusiness.id}`,
                    refreshToken,
                );
                if (res?.data?.rows) {
                    setTemplateOptions(res.data.rows);
                }
            } catch (error) {
                console.log('get error fetching quote templates---:', error);
            } finally {
                setTemplateOptionsLoading(false);
            }
        })();
    }, [paramscurrBusiness?.id]);
    /*START cocktail and product api call*/
    const getAllSystemTypes = async () => {
        try {
            const res = await GET(`${getAllTypes}`, refreshToken);
            if (res.data) {
                if (res.data?.data?.length > 0) {
                    const cocktails =
                        res.data.data.find((x: any) => x.category === 'cocktails')?.types || [];
                    const products =
                        res.data.data.find((x: any) => x.category === 'products')?.types || [];
                    // ---------- Step 2: remove duplicates from products and beer_cocktail ----------
                    const filteredProducts = products.filter(
                        (item: any) => !cocktails.includes(item) && item !== 'beer_cocktail',
                    ); // ---------- Step 3: helper to capitalize ----------
                    const capitalize = (str: string) => {
                        return str
                            .replace(/_/g, ' ')
                            .split(' ')
                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ');
                    };
                    // ---------- Step 4: format into new structure ----------
                    const formatted = [
                        ...cocktails.map((item: any, i: number) => ({
                            id: `cocktail-${Date.now()}-${i}`,
                            type: 'cocktails',
                            name: capitalize(item),
                            value: item,
                        })),
                        ...filteredProducts.map((item: any, i: number) => ({
                            id: `product-${Date.now()}-${i}`,
                            type: 'products',
                            name: capitalize(item),
                            value: item,
                        })),
                    ];
                    const others = formatted.filter((item) => item.value === 'other');
                    const withoutOthers = formatted.filter((item) => item.value !== 'other');
                    return [...withoutOthers, ...others];
                }
            }
        } catch (error) {
            console.log('get error in storelist---:', error);
        }
    };
    const getPackageCocktails = async (paramsType?: any) => {
        try {
            const body = {
                business_id: paramscurrBusiness?.id,
                type: paramsType || cocktailSelectedType?.value,
            };
            const res = await POST(`${getCocktailsByType}`, body, refreshToken);
            if (res?.data?.data) {
                const response = res?.data?.data || [];
                setPackageCocktails([
                    ...response,
                    { name: 'Custom', value: 'custom', id: 'custom' },
                ]);
                return response;
            }
        } catch (error) {
            console.log('get error in packages cocktail---:', error);
        }
    };
    // A brand option can be a raw product ({id, ...}) or a cocktail ingredient ({product: {id, ...}, ...})
    const getBrandProductId = (brand: any): string | undefined =>
        brand?.product?.id ?? brand?.id ?? brand?.value;
    const isPreferredBrand = (brand: any) => {
        const productId = getBrandProductId(brand);
        if (!productId) return { is_prefer: false, bonus: 0 };
        const match = preferredProducts.find((p: any) => p.prefer1?.id === productId);
        return match ? { is_prefer: true, bonus: Number(match.bonus) || 0 } : { is_prefer: false, bonus: 0 };
    };
    const sortPreferredFirst = (list: any[]) => {
        return [...list].sort((a, b) => {
            const aPref = isPreferredBrand(a).is_prefer ? 1 : 0;
            const bPref = isPreferredBrand(b).is_prefer ? 1 : 0;
            return bPref - aPref;
        });
    };
    const getProductBrandApi = async (paramsType?: any) => {
        try {
            setBrandLoading(true);
            const body = {
                store_id: '01988aae-0546-752a-a756-79d23b57dd14',
                type: paramsType || cocktailSelectedType?.value,
            };
            const res = await POST(`${getProductBrand}`, body, refreshToken);
            if (res.data?.data) {
                const response = res.data?.data || [];
                setStoreBrand(sortPreferredFirst(response));
                return [...response];
                // return [...response, { name: 'Other', value: 'other', id: 'other' }];
            }
        } catch (error) {
            console.log('get error in store brand---:', error);
        } finally {
            setBrandLoading(false);
        }
    };
    // Builds the cocktailItem[] rows (cocktail/product/brand lookups resolved) for a given
    // quote payload — shared by loading an existing quote and applying a template's package.
    const buildCocktailItemsFromQuote = async (data: any, allTypes: any[] = []) => {
        return Promise.all(
            (data?.quote_cocktails || []).map(async (d: any) => {
                let selectedCocktail;
                let selectedProduct;
                const findedCocktail =
                    (await getPackageCocktails(d?.type)?.then((res) =>
                        res?.find((c: any) => c.id === d?.cocktail_id),
                    )) || null;
                selectedCocktail = findedCocktail;
                //below logic is for cocktail selection
                if (selectedCocktail == null) {
                    selectedCocktail =
                        d?.type === 'other'
                            ? null
                            : { name: 'Custom', value: 'custom', id: 'custom' };
                }
                //below logic is for product brand selection
                if (d?.product_id == null) {
                    selectedProduct =
                        findedCocktail == null
                            ? null
                            : { name: 'Other', value: 'other', id: 'other' };
                }
                if (d?.drink_type === 'cocktail' && d?.product_id) {
                    selectedCocktail?.cocktail_ingredients?.map((ing: any) => {
                        ing?.product_id === d?.product_id
                            ? (selectedProduct = ing)
                            : null;
                    });
                }
                if (d?.drink_type === 'cocktail' && selectedCocktail?.id === 'custom') {
                    selectedProduct =
                        (await getProductBrandApi(d?.type)?.then((res) =>
                            res?.find((p: any) => p.id === d?.product_id),
                        )) || null;
                }
                if (d?.drink_type !== 'cocktail' && d?.product_id) {
                    selectedProduct =
                        (await getProductBrandApi(d?.type)?.then((res) =>
                            res?.find((p: any) => p.id === d?.product_id),
                        )) || null;
                }
                return {
                    id: d?.id,
                    provider: CocktailProviders.find((p) => p.value === d?.provider) || null,
                    qty: Number(d?.quantity),
                    type: allTypes?.find((t: any) => t.value === d?.type) || null,
                    cocktails: selectedCocktail,
                    brand: selectedProduct,
                    brandName: '',
                    pricePerItem: Number(d?.price_per_item),
                };
            }),
        );
    };
    // Applies a template's notes, contract, and additional info onto the quote currently
    // being built. Called from applyPackageTemplate as part of applying the Bartender Package
    // template, since Additional Information no longer has its own template selector.
    const applyAdditionalInfoFields = (res: any, data: any) => {
        const selectedContract = res?.data?.quoteContract;
        setCurrentContract(
            selectedContract ? { ...selectedContract, name: selectedContract?.title } : undefined,
        );
        setNotes(data?.notes || '');
        setValue('event.additionalInfo', data?.additional_info || '', { shouldDirty: true });
    };
    // Applies a saved template's Bartender Package items, notes, contract, and additional
    // info onto the quote currently being built, leaving event/client fields untouched.
    const applyPackageTemplate = async (templateId: string) => {
        if (!templateId) return;
        try {
            setApplyingPackageTemplate(true);
            const res = await GET(`${createQuote}/${templateId}`, refreshToken);
            const data = res?.data?.data;
            if (!data) return;
            replaceItems(mapBartenderPackagesForFrontend(data.quote_bartender_package || []));
            applyAdditionalInfoFields(res, data);
            setHasUserInteractedWithForm(true);
        } catch (error) {
            console.log('get error applying package template---:', error);
            Alert({ type: 'error', message: 'Unable to load selected template' });
        } finally {
            setApplyingPackageTemplate(false);
        }
    };
    const handleSelectPackageTemplate = (templateId: string) => {
        setSelectedPackageTemplateId(templateId);
        if (templateId) applyPackageTemplate(templateId);
    };
    /*END cocktail and product api call*/
    const { fields, append, update, remove, replace: replaceItems } = useFieldArray({ control, name: 'items' });
    const {
        fields: cocktailFields,
        append: cocktailAppend,
        update: cocktailUpdate,
        remove: cocktailRemove,
        replace: replaceCocktailItems,
    } = useFieldArray({ control, name: 'cocktailItem' });
    // "Select Template" dropdown for the Bartender Package section. Applying a template here
    // also fills in the Additional Information & Contract fields (see applyPackageTemplate).
    const renderTemplateSelect = (
        selectedTemplateId: string,
        onSelectTemplate: (templateId: string) => void,
        isLoading: boolean,
        widthClassName = 'w-full',
    ) => {
        if (templateOptions.length === 0) return null;
        return (
            <div className={`${widthClassName} mb-4`}>
                <div className='flex items-center gap-2 mb-2 pl-2'>
                    <p className='font-[500] text-[12px] text-[#19191C]'>
                        Select Template
                    </p>
                    {isLoading && <Loader color='bg-red-500' />}
                </div>
                <SearchSelect
                    options={templateOptions.map((t) => ({
                        name: t?.template_title || 'Untitled Template',
                        value: t?.id,
                    }))}
                    value={
                        selectedTemplateId
                            ? {
                                value: selectedTemplateId,
                                name:
                                    templateOptions.find((t) => t.id === selectedTemplateId)
                                        ?.template_title || 'Untitled Template',
                            }
                            : null
                    }
                    onChange={(selectedValue: any) => {
                        onSelectTemplate(selectedValue?.value || '');
                    }}
                    placeholder={isLoading ? 'Loading template...' : 'Select template'}
                    isLoading={isLoading}
                    isClearable
                />
            </div>
        );
    };
    // watches to drive validation & previews (single source of truth)
    const startTimeWatch = watch('event.startTime');
    const drinkerWatch = watch('event.drinker');
    const numberOfGuests = watch('event.noOfGuests');
    const startTime = watch('event.startTime');
    const endTime = watch('event.endTime');
    // Watches
    const currency = watch('currency');
    const draft = watch('draft');
    const cocktail = watch('cocktail');
    const isCocktailSaveDisabled = (() => {
        if (!cocktail?.provider) return true;
        if (!cocktail?.type) return true;
        const isProducts = cocktailSelectedType?.type === 'products';
        const isOther = cocktail?.type?.value === 'other';
        if (!isProducts && !isOther && !cocktail?.cocktails) return true;
        const isNonCustomCocktailSelected =
            cocktail?.type?.type === 'cocktails' &&
            cocktail?.cocktails &&
            cocktail?.cocktails?.id !== 'custom' &&
            cocktail?.cocktails?.value !== 'custom';
        if (!isOther && !isNonCustomCocktailSelected && !cocktail?.brand) return true;
        const isBrandNameVisible = isOther || cocktail?.brand?.id === 'other';
        if (isBrandNameVisible && !cocktail?.brandName?.trim()) return true;
        return false;
    })();
    const items = watch('items');
    const cocktailItem = watch('cocktailItem');
    const discount = watch('discount');
    const eventDetails = watch('event');
    const payment = watch('payment');
    // payment Watches
    const fullPayment = watch('payment.fullPayment');
    const firstPaymentDueDate = watch('payment.firstPaymentDueDate');
    const secondPaymentDueDate = watch('payment.secondPaymentDueDate');
    //max alcohol provided
    const maxAlcohol = cocktailFields
        .map((item) => cocktailPrice(item))
        .reduce((a, b) => nnum(a) + nnum(b), 0);
    // 5% reward-point bonus earned on cocktail/alcohol lines using a business-preferred brand
    const preferredBrandBonus = cocktailFields.reduce((acc, item) => {
        return isPreferredBrand(item.brand).is_prefer
            ? acc + nnum(cocktailPrice(item)) * 0.05
            : acc;
    }, 0);
    // Keep derived fields up to date when using default provider
    useEffect(() => {
        if (draft?.provider !== 'companyProvide') return;
        const qty = Math.max(0, Math.floor(nnum(draft.qty)));
        const price = nnum(draft.pricePerItem);
        const total = price * qty;
        setValue('draft.lineTotal', total?.toFixed(2));
    }, [draft?.provider, draft?.qty, draft?.pricePerItem, setValue]);
    const toQuoteItem = (l: ItemLine): QuoteItem => {
        return {
            id: l.id,
            name: String(l.name || 'Untitled Item'),
            qty: Math.max(0, Math.floor(nnum(l.qty))),
            amount: nnum(l.lineTotal),
            provider: l.provider,
        };
    };
    const saveDraftIntoItems = () => {
        const d = getValues('draft');
        const id = d.id || (globalThis.crypto?.randomUUID?.() ?? String(Date.now()));
        // Compute derived if needed
        let pricePerItem = 0;
        let lineTotal = 0;
        const qty = Math.max(0, Math.floor(nnum(d.qty)));
        if (draft.provider === 'companyProvide') {
            pricePerItem = nnum(d.pricePerItem);
            lineTotal = pricePerItem * qty;
        } else {
            pricePerItem = 0;
            lineTotal = 0; // hidden as requested
        }
        const payload: ItemLine = {
            id,
            provider: d.provider,
            name: d.name,
            qty,
            costPerItem: d.costPerItem,
            profitPerItem: d.profitPerItem,
            pricePerItem,
            lineTotal,
        };
        if (d.editingIndex !== null && d.editingIndex >= 0 && d.editingIndex < fields.length) {
            update(d.editingIndex, payload);
        } else {
            append(payload);
        }
        // Clear draft
        setValue('draft', {
            id: '',
            provider: '',
            name: '',
            qty: '',
            costPerItem: '',
            profitPerItem: '',
            pricePerItem: '',
            lineTotal: '',
            editingIndex: null,
        });
    };
    const handleBPEdit = (id: string) => {
        const idx = fields.findIndex((f) => f.id === id);
        if (idx === -1) return;
        const it = getValues(`items.${idx}` as const) as ItemLine;
        setValue('draft', { ...it, editingIndex: idx });
    };
    const handleBPRemove = (id: string) => {
        const idx = fields.findIndex((f) => f.id === id);
        if (idx !== -1) remove(idx);
    };
    const handleBPAddClick = () => saveDraftIntoItems();
    const handleCocktailAddClick = () => saveCocktailIntoItems();
    const saveCocktailIntoItems = () => {
        const c = getValues('cocktail');
        const id = c.id || (globalThis.crypto?.randomUUID?.() ?? String(Date.now()));
        const isEdit = c.editingIndex !== null && c.editingIndex >= 0 && c.editingIndex < cocktailFields.length;
        const estDrinks = Number(eventDetails?.estDrinks) || 0;
        const noOfGuests = Number(eventDetails?.noOfGuests) || 0;
        const currentProductCount = cocktailFields.filter((f) => f.type?.type === 'products').length;
        const currentCocktailCount = cocktailFields.filter((f) => f.type?.type === 'cocktails').length;
        const oldItem = isEdit && c.editingIndex != null ? cocktailFields[c.editingIndex] : null;

        const isNonCustomCocktail =
            c?.type?.type === 'cocktails' &&
            c?.cocktails &&
            c.cocktails?.id !== 'custom' &&
            c.cocktails?.value !== 'custom';
        const ingredientsWithProduct = isNonCustomCocktail
            ? (Array.isArray(c.cocktails?.cocktail_ingredients) ? c.cocktails.cocktail_ingredients : []).filter(
                (ing: any) => ing?.product && ing?.product?.type !== 'mixers',
            )
            : [];

        const numNewCocktailLines = isNonCustomCocktail ? Math.max(1, ingredientsWithProduct.length) : 1;
        const productCountAfterSave =
            currentProductCount - (oldItem?.type?.type === 'products' ? 1 : 0) + (c?.type?.type === 'products' ? 1 : 0);
        const cocktailCountAfterSave =
            currentCocktailCount -
            (oldItem?.type?.type === 'cocktails' ? 1 : 0) +
            (isNonCustomCocktail ? ingredientsWithProduct.length : c?.type?.type === 'cocktails' ? 1 : 0);
        const wineProductCountAfterSave =
            cocktailFields.filter((f) => f.type?.type === 'products' && isWineProduct(f.type?.value)).length -
            (oldItem?.type?.type === 'products' && isWineProduct(oldItem?.type?.value) ? 1 : 0) +
            (c?.type?.type === 'products' && isWineProduct(c?.type?.value) ? 1 : 0);
        const beerProductCountAfterSave =
            cocktailFields.filter((f) => f.type?.type === 'products' && isBeerProduct(f.type?.value)).length -
            (oldItem?.type?.type === 'products' && isBeerProduct(oldItem?.type?.value) ? 1 : 0) +
            (c?.type?.type === 'products' && isBeerProduct(c?.type?.value) ? 1 : 0);
        const hasBoth = productCountAfterSave > 0 && cocktailCountAfterSave > 0;
        const effectiveEstDrinksCocktails = hasBoth ? estDrinks * 0.8 : estDrinks;
        // When hasBoth: 10% wines + 10% beers if both present; else full 20% to the one present.
        const effectiveEstDrinksWines = hasBoth
            ? (beerProductCountAfterSave > 0 ? estDrinks * 0.1 : estDrinks * 0.2)
            : beerProductCountAfterSave > 0
                ? estDrinks * 0.5
                : estDrinks;
        const effectiveEstDrinksBeers = hasBoth
            ? (wineProductCountAfterSave > 0 ? estDrinks * 0.1 : estDrinks * 0.2)
            : wineProductCountAfterSave > 0
                ? estDrinks * 0.5
                : estDrinks;
        const effectiveEstDrinksOtherProducts = hasBoth ? estDrinks * 0.2 : estDrinks;
        const countAfterSave = isEdit ? cocktailFields.length : cocktailFields.length + (isNonCustomCocktail ? ingredientsWithProduct.length : 1);
        const cocktailLineCount = cocktailCountAfterSave || 1;
        console.log('cocktailLineCount------------:', cocktailLineCount)
        console.log('effectiveEstDrinksCocktails------------:', effectiveEstDrinksCocktails)
        const numberOfCocktailsPerLine = Math.max(1, Math.ceil(effectiveEstDrinksCocktails / cocktailLineCount));

        const getProductEffectiveDrinks = (productValue: string | undefined) => {
            if (isWineProduct(productValue))
                return { effective: effectiveEstDrinksWines, lineCount: wineProductCountAfterSave || 1 };
            if (isBeerProduct(productValue))
                return { effective: effectiveEstDrinksBeers, lineCount: beerProductCountAfterSave || 1 };
            return { effective: effectiveEstDrinksOtherProducts, lineCount: productCountAfterSave || 1 };
        };

        if (isEdit && c.editingIndex != null) {
            const it = getValues(`cocktailItem.${c.editingIndex}` as const) as CocktailItemLine;
            let qtyValue: number;
            if (c?.type?.type === 'products') {
                const { effective, lineCount } = getProductEffectiveDrinks(c?.type?.value);
                qtyValue = Math.max(1, Math.ceil(getProductQuantity(c?.brand, c?.type?.value, noOfGuests, lineCount, effective)));
            } else if (c?.brand) {
                const qtyResult = getCocktailProduct(c.brand, numberOfCocktailsPerLine, c?.cocktails?.id);
                qtyValue = typeof qtyResult?.quantity === 'number' ? qtyResult.quantity : Math.max(1, Math.floor(nnum(c.qty)));
            } else {
                qtyValue = Math.max(1, Math.floor(nnum(c.qty)));
            }
            cocktailUpdate(c.editingIndex, {
                ...it,
                provider: c?.provider,
                type: c?.type,
                cocktails: c?.cocktails,
                brand: c?.brand,
                brandName: c?.brandName,
                qty: qtyValue,
            });
        } else if (isNonCustomCocktail && ingredientsWithProduct.length > 0) {
            // One row per ingredient (brand) so calculation works per row
            ingredientsWithProduct.forEach((ingredient: any) => {
                const qtyResult = getCocktailProduct(ingredient, numberOfCocktailsPerLine, c?.cocktails?.id);
                const qtyValue = typeof qtyResult?.quantity === 'number' ? qtyResult.quantity : 1;
                cocktailAppend({
                    id: globalThis.crypto?.randomUUID?.() ?? String(Date.now() + Math.random()),
                    provider: c?.provider,
                    type: c?.type,
                    cocktails: c?.cocktails,
                    brand: ingredient,
                    brandName: c?.brandName,
                    qty: qtyValue,
                });
            });
        } else {
            let qtyValue: number;
            if (c?.type?.type === 'products') {
                const { effective, lineCount } = getProductEffectiveDrinks(c?.type?.value);
                qtyValue = Math.max(1, Math.ceil(getProductQuantity(c?.brand, c?.type?.value, noOfGuests, lineCount, effective)));
            } else if (c?.brand) {
                const qtyResult = getCocktailProduct(c.brand, numberOfCocktailsPerLine, c?.cocktails?.id);
                qtyValue = typeof qtyResult?.quantity === 'number' ? qtyResult.quantity : Math.max(1, Math.floor(nnum(c.qty)));
            } else {
                qtyValue = Math.max(1, Math.floor(nnum(c.qty)));
            }
            cocktailAppend({
                id,
                provider: c?.provider,
                type: c?.type,
                cocktails: c?.cocktails,
                brand: c?.brand,
                brandName: c?.brandName,
                qty: qtyValue,
            });
        }

        setValue('cocktail', {
            id: '',
            provider: null as any,
            qty: '',
            type: null as any,
            cocktails: null as any,
            brand: null as any,
            brandName: null as any,
            pricePerItem: '',
            editingIndex: null,
        });
    };
    const handleCocktailEdit = (id: string) => {
        const idx = cocktailFields.findIndex((f) => f.id === id);
        if (idx === -1) return;
        const it = getValues(`cocktailItem.${idx}` as const) as CocktailItemLine;
        setValue('cocktail', { ...it, editingIndex: idx });
    };
    const handleCocktailRemove = (id: string) => {
        const idx = cocktailFields.findIndex((f) => f.id === id);
        if (idx !== -1) cocktailRemove(idx);
    };
    const handleCocktailRemoveGroup = (ids: string[]) => {
        const indices = ids
            .map((id) => cocktailFields.findIndex((f) => f.id === id))
            .filter((i) => i !== -1);
        const sorted = [...new Set(indices)].sort((a, b) => b - a);
        sorted.forEach((i) => cocktailRemove(i));
    };
    const handleCocktailQtyChange = (index: number, qty: number) => {
        const it = getValues(`cocktailItem.${index}` as const) as CocktailItemLine;
        if (it) cocktailUpdate(index, { ...it, qty });
    };
    // console.log('cocktailFields---------:', cocktailFields)
    // Recalculate qty for all drink items when estDrinks, guests, or list length changes
    // Allocation: cocktails 80%; within products 20% → 10% wines, 10% beers (when both cocktails and products)
    useEffect(() => {
        const prevLen = prevCocktailLength.current;
        prevCocktailLength.current = cocktailFields.length;
        // Always recalculate when the user adds/removes items (prevLen > 0 skips the initial load).
        // For estDrinks/guests-only changes, still require the user to have clicked a pace pill.
        const itemsChanged = cocktailFields.length !== prevLen && prevLen > 0;
        if (!drinkerClickedByUser.current && !itemsChanged) return;
        if (cocktailFields.length === 0) return;
        const estDrinks = Number(eventDetails?.estDrinks) || 0;
        const noOfGuests = Number(eventDetails?.noOfGuests) || 0;
        const productCount = cocktailFields.filter((f) => f.type?.type === 'products').length;
        const cocktailCount = cocktailFields.filter((f) => f.type?.type === 'cocktails').length;
        const wineProductCount = cocktailFields.filter(
            (f) => f.type?.type === 'products' && isWineProduct(f.type?.value),
        ).length;
        const beerProductCount = cocktailFields.filter(
            (f) => f.type?.type === 'products' && isBeerProduct(f.type?.value),
        ).length;
        const hasBoth = productCount > 0 && cocktailCount > 0;
        const effectiveEstDrinksCocktails = hasBoth ? estDrinks * 0.8 : estDrinks;
        // When hasBoth: 10% wines + 10% beers if both present; else full 20% to the one present.
        const effectiveEstDrinksWines = hasBoth
            ? (beerProductCount > 0 ? estDrinks * 0.1 : estDrinks * 0.2)
            : beerProductCount > 0
                ? estDrinks * 0.5
                : estDrinks;
        const effectiveEstDrinksBeers = hasBoth
            ? (wineProductCount > 0 ? estDrinks * 0.1 : estDrinks * 0.2)
            : wineProductCount > 0
                ? estDrinks * 0.5
                : estDrinks;
        const effectiveEstDrinksOtherProducts = hasBoth ? estDrinks * 0.2 : estDrinks;
        const cocktailLineCount = cocktailCount || 1;
        const numberOfCocktailsPerLine = Math.max(1, Math.ceil(effectiveEstDrinksCocktails / cocktailLineCount));

        const getProductEffectiveDrinks = (productValue: string | undefined) => {
            if (isWineProduct(productValue))
                return { effective: effectiveEstDrinksWines, lineCount: wineProductCount || 1 };
            if (isBeerProduct(productValue))
                return { effective: effectiveEstDrinksBeers, lineCount: beerProductCount || 1 };
            return { effective: effectiveEstDrinksOtherProducts, lineCount: productCount || 1 };
        };

        cocktailFields.forEach((_, idx) => {
            const it = getValues(`cocktailItem.${idx}` as const) as CocktailItemLine;
            let newQty: number | undefined;
            if (it?.type?.type === 'products') {
                const { effective, lineCount } = getProductEffectiveDrinks(it.type?.value);
                newQty = getProductQuantity(it.brand, it.type?.value, noOfGuests, lineCount, effective);
                newQty = Math.max(1, Math.ceil(newQty));
            } else if (it?.brand) {
                const qtyResult = getCocktailProduct(it.brand, numberOfCocktailsPerLine, it?.cocktails?.id);
                newQty = qtyResult?.quantity ?? undefined;
            }
            if (typeof newQty === 'number' && Number(it?.qty) !== newQty) {
                cocktailUpdate(idx, { ...it, qty: newQty });
            }
        });
    }, [eventDetails?.estDrinks, eventDetails?.noOfGuests, cocktailFields.length]);

    // --- Recalculate estDrinks whenever guests, time, or drinking pace changes ---
    useEffect(() => {
        //if (!drinkerClickedByUser.current) return;
        if (numberOfGuests && startTime && endTime && drinkerWatch) {
            const eventLengthHours = Math.ceil(diffMinutesAllowNextDay(startTime, endTime) / 60);
            if (drinkerWatch == 'heavy') {
                const heavyDrinker = Number(eventLengthHours) * 2 * Number(numberOfGuests);
                setValue('event.estDrinks', heavyDrinker, { shouldDirty: false });
            } else if (drinkerWatch == 'medium') {
                const mediumDrinker = (Number(eventLengthHours) + 1) * Number(numberOfGuests);
                setValue('event.estDrinks', mediumDrinker, { shouldDirty: false });
            } else if (drinkerWatch == 'light') {
                const lightDrinker = Number(eventLengthHours) * Number(numberOfGuests);
                setValue('event.estDrinks', lightDrinker, { shouldDirty: false });
            } else {
                setValue('event.estDrinks', undefined as any, { shouldDirty: false });
            }
        }
    }, [numberOfGuests, startTime, endTime, drinkerWatch, quote]);

    // --- Pricing Calculator sync: estimated Total / Profit for the selected package ---
    const calculatorPackages = useMemo<Package[]>(
        () => packageList.filter((p) => p?.id && p.id !== 'other'),
        [packageList],
    );
    // Default to the Standard package (same as the Pricing Calculator) once packages load.
    useEffect(() => {
        if (calcPackageId || calculatorPackages.length === 0) return;
        const standard =
            calculatorPackages.find((p) => p.type === PackageType.Standard) || calculatorPackages[0];
        setCalcPackageId(standard.id);
    }, [calculatorPackages, calcPackageId]);
    const calcPackage = calculatorPackages.find((p) => p.id === calcPackageId);
    const calcGuests = nnum(numberOfGuests);
    const calcHours =
        startTime && endTime ? Math.ceil(diffMinutesAllowNextDay(startTime, endTime) / 60) : 0;
    // Business location (PostGIS point: coordinates = [lng, lat]) for the travel cost distance.
    // Use it from currBusiness when present, otherwise load the business record.
    useEffect(() => {
        const toGeo = (business: any): GeoPoint | null => {
            const coordinates = business?.location?.coordinates;
            return coordinates?.length === 2
                ? { lat: Number(coordinates[1]), lng: Number(coordinates[0]) }
                : null;
        };
        const fromState = toGeo(paramscurrBusiness);
        if (fromState) {
            setBusinessGeo(fromState);
            return;
        }
        if (!paramscurrBusiness?.id) return;
        let cancelled = false;
        GET(`${getCompanyApi}/${paramscurrBusiness.id}`, refreshToken)
            .then((res) => {
                if (!cancelled) setBusinessGeo(toGeo(res?.data));
            })
            .catch((error) => console.log('get error fetching business location---:', error));
        return () => {
            cancelled = true;
        };
    }, [paramscurrBusiness?.id]);
    // Travel cost = package delivery fee + mileage beyond the free miles (same distance and
    // mileage rules as the Pricing Calculator / client checkout).
    const eventDistance =
        businessGeo && place?.lat && place?.lng
            ? getStraightLineMiles(businessGeo, { lat: place.lat, lng: place.lng })
            : null;
    const mileageCharge =
        calcPackage && eventDistance !== null && (calcPackage.radius || 0) > FREE_MILES
            ? getMileageCharge(eventDistance, (calcPackage.mileageFee || 0) / 100, FREE_MILES)
            : 0;
    const travelCost = calcPackage ? (calcPackage.deliveryFee || 0) / 100 + mileageCharge : 0;
    const calcPackageTotals = useMemo(() => {
        if (!calcPackage || calcGuests <= 0 || calcHours <= 0) return null;
        return calculateEstimatedTotalsFromPackage(
            // Package delivery fee (cents) is replaced with the full travel cost
            { ...calcPackage, deliveryFee: includeTravelCost ? Math.round(travelCost * 100) : 0 },
            { guests: calcGuests, hours: calcHours, drinker: drinkerWatch as Drinker },
            {
                includeMandatoryPortableBar: includePortableBar,
                includeMandatoryIceAndCooler: includeIceAndCooler,
                // Calculator only counts custom supplies for custom packages
                includeCustomSupplies: calcPackage.type === PackageType.CustomPackage,
                paymentProcessingFeeRate: 0.05, // Quote rate
                // Percentage comes from the package's saved gratuity (tipsFee)
                includeTips: includeGratuity,
            },
        );
    }, [
        calcPackage,
        calcGuests,
        calcHours,
        drinkerWatch,
        includePortableBar,
        includeIceAndCooler,
        includeTravelCost,
        travelCost,
        includeGratuity,
    ]);
    // --- Enforce end >= start + 1h validation ---
    const ensureEndAtLeast30Min = (end: string) => {
        if (!startTimeWatch || !end) return true;
        const delta = diffMinutesAllowNextDay(startTimeWatch, end);
        return delta >= MIN_EVENT_GAP_MINUTES || 'End time must be at least 30 minutes after start time';
    };
    // --- End dropdown options filtered to >= +1h from start ---
    const endOptions = useMemo(() => {
        if (!startTime) return [];
        const options = getValidEndOptions(startTime, TimeOptions);
        return options.map((opt) => ({
            value: opt.value,
            name: opt.name || opt.value,
        }));
    }, [startTime]);
    // --- Auto-select end time to start + 30 minutes when start changes ---
    // Only do this when there's no existing end time (new quote / user hasn't picked one yet),
    // so we don't overwrite a saved value when editing an existing quote.
    useEffect(() => {
        if (!startTime) {
            setValue('event.endTime', '', { shouldValidate: false, shouldDirty: false });
            return;
        }
        if (endTime) {
            // existing end time (e.g. loaded from server or already chosen) – keep it
            return;
        }
        const validList = getValidEndOptions(startTime, TimeOptions);
        const endTime30Min = getEndTimeMinutesAfter(startTime, MIN_EVENT_GAP_MINUTES, TimeOptions);
        const suggestedEnd = endTime30Min ?? validList[0]?.value ?? '';
        setValue('event.endTime', suggestedEnd, { shouldValidate: true, shouldDirty: false });
        trigger('event.endTime');
    }, [startTime, endTime, setValue, trigger]);
    // keep slider in sync when an accordion opens
    useEffect(() => {
        if (openIndex !== null) setPageIndex(openIndex);
    }, [openIndex]);
    const onAddressSelected = (
        address: string,
        lat: number,
        lng: number,
        city: string,
        state: string,
        zip: string,
        timezone: string,
    ) => {
        // console.log('address selected in quote builder:', timezone);
        setPlace({ address, lat, lng, city, state, zip, timezone });
        setValue('event.eventAddress', address, { shouldValidate: true });
    };
    // keep accordion in sync when slider changes
    const handlePageChange = (i: number) => {
        setPageIndex(i);
        setOpenIndex(i);
    };
    const toggleAt = (idx: number) => setOpenIndex((prev) => (prev === idx ? null : idx));
    // preview slides (match your accordion order). If your previews need form data,
    // pluck with watch() inside those components or pass watch() values down.
    const subTotalBP = React.useMemo(
        () => items.reduce((a, c) => a + nnum(c.lineTotal), 0),
        [items],
    );
    const platformFeeAmount = React.useMemo(
        () => (subTotalBP > 0 ? calculatePackagePlatformFee(subTotalBP, platformFee) : 0),
        [subTotalBP, platformFee],
    );
    const afterPlatformFee = Math.max(0, subTotalBP - platformFeeAmount);
    const discountedSubTotalBP = calculateDiscountedTotal(subTotalBP, discount);
    const minDeposit = Math.min(platformFeeAmount, discountedSubTotalBP);
    const discountAmountRaw = React.useMemo(() => {
        if (!discount?.enabled) return 0;
        const amt = Number(discount.amount) || 0;
        const pct = Number(discount.percentage) || 0;
        if (amt > 0) return amt;
        if (pct > 0) return (subTotalBP * pct) / 100;
        return 0;
    }, [discount?.enabled, discount?.amount, discount?.percentage, subTotalBP]);
    const effectiveDiscount = Math.min(discountAmountRaw, afterPlatformFee);
    const yourFee = Math.max(0, afterPlatformFee - effectiveDiscount);
    // const discountedSubTotalBP =  calculateDiscountedTotal(subTotalBP, discount);
    // const totalBP =
    //     subTotalBP > 0
    //         ? calculateDiscountedTotal(
    //             subTotalBP + calculatePlatformFee(subTotalBP, platformFee),
    //             discount,
    //         )
    //         : 0;
    const totalBP =
        subTotalBP > 0 ?
            discountedSubTotalBP
            : 0;
    // const totalBP =
    //     subTotalBP > 0 ?
    //         discountedSubTotalBP + calculatePlatformFee(subTotalBP, platformFee)
    //         : 0;
    const isMoreThenSevenDays = isMoreThan7DaysAway(eventDetails.eventDate);

    // 2nd payment due date can be picked within the last 3 days before the event.
    // Window: eventDate - 3 days ... eventDate - 1 day (inclusive)
    const secondPaymentDueDateMin = eventDetails.eventDate
        ? shiftCalendarDate(eventDetails.eventDate, -3)
        : null;
    const secondPaymentDueDateMax = eventDetails.eventDate
        ? shiftCalendarDate(eventDetails.eventDate, -1)
        : null;
    // First payment due date must be at least 1 day before the 2nd payment due date.
    // When 2nd payment is not configurable, fall back to 1 day before event.
    // Clamp to today so the calendar is never fully disabled when event is very close.
    const todayDate = new Date(
        new Date().getFullYear(), new Date().getMonth(), new Date().getDate(),
    );
    const rawFirstPaymentDueDateMax =
        !fullPayment && isMoreThenSevenDays && secondPaymentDueDate
            ? shiftCalendarDate(secondPaymentDueDate, -1)
            : eventDetails.eventDate
                ? shiftCalendarDate(eventDetails.eventDate, 0)
                : null;
    const firstPaymentDueDateMax =
        rawFirstPaymentDueDateMax && rawFirstPaymentDueDateMax < todayDate
            ? todayDate
            : rawFirstPaymentDueDateMax;
    const estimatedTotalCost =
        maxAlcohol > 0 || totalBP > 0
            ? maxAlcohol + totalBP + calculatePlatformFee(maxAlcohol, stateTaxFee)
            : 0;
    // When event date is within 7 days (or no date): force full payment on and restrict toggle.
    // When >7 days: leave the user's/saved choice alone — don't override it back to false.
    useEffect(() => {
        const moreThan7 = eventDetails.eventDate != null && isMoreThan7DaysAway(eventDetails.eventDate);
        if (!moreThan7) {
            setValue('payment.fullPayment', true, { shouldDirty: false });
        }
    }, [eventDetails.eventDate, setValue]);

    useEffect(() => {
        if (!eventDetails.eventDate) return;
        if (fullPayment || !isMoreThenSevenDays) return;
        // Don't clobber an already-set value (loaded from DB or manually picked).
        if (secondPaymentDueDate) return;

        setValue(
            'payment.secondPaymentDueDate',
            shiftCalendarDate(eventDetails.eventDate, -3),
            { shouldDirty: false, shouldValidate: false },
        );
    }, [
        eventDetails.eventDate,
        fullPayment,
        isMoreThenSevenDays,
        secondPaymentDueDate,
        setValue,
    ]);

    useEffect(() => {
        if (fullPayment || !isMoreThenSevenDays) return;
        if (!secondPaymentDueDate || !firstPaymentDueDate) return;
        const max = shiftCalendarDate(secondPaymentDueDate, -1);
        if (firstPaymentDueDate > max) {
            // Clamp first due date so it stays valid when 2nd due date changes.
            setValue('payment.firstPaymentDueDate', max, {
                shouldDirty: false,
                shouldValidate: false,
            });
        }
    }, [secondPaymentDueDate, firstPaymentDueDate, setValue]);

    useEffect(() => {
        if (fullPayment || !isMoreThenSevenDays) {
            // Paid in full (toggled on) or event within 7 days → force full payment
            setValue('payment.deposit', discountedSubTotalBP, {
                shouldValidate: true,
                shouldDirty: false,
            });
        } else if (subTotalBP > 0 && (nnum(paymentDeposit) === 0 || paymentDeposit === '')) {
            // Items just added and no deposit set yet → default to 50%
            const defaultDeposit = Math.max(minDeposit, discountedSubTotalBP * 0.5);
            setValue('payment.deposit', defaultDeposit, {
                shouldValidate: true,
                shouldDirty: false,
            });
        } else if (nnum(paymentDeposit) > discountedSubTotalBP) {
            // Discount reduced total below current deposit → cap down
            setValue('payment.deposit', discountedSubTotalBP, {
                shouldValidate: true,
                shouldDirty: false,
            });
        }
        setValue(
            'payment.totalDeposit',
            (isNaN(Number(payment.deposit)) ? 0 : Number(payment.deposit)),
            // +
            // calculatePlatformFee(subTotalBP, platformFee),
            {
                shouldValidate: true,
                shouldDirty: false,
            },
        );
        setValue('payment.secondPayment', nnum(discountedSubTotalBP) - nnum(paymentDeposit), {
            shouldDirty: false,
        });
    }, [fullPayment, isMoreThenSevenDays, subTotalBP, discountedSubTotalBP, paymentDeposit]);

    const closeCardModal = () => {
        setIsCardModalOpen(false);
    };

    const slides = [
        <div key='slide-0'>
            <QuotePreview event={eventDetails} />
        </div>,
        <div key='slide-1'>
            <BartenderPreview items={items} discount={discount} platformFee={platformFee} />
        </div>,
        <div key='slide-2'>
            <CocktailPreview
                items={cocktailItem}
                subTotal={maxAlcohol}
                numberOfCocktail={eventDetails.estDrinks}
                platformFee={alcoholPlatformFee}
                stateTaxFee={stateTaxFee}
            />
        </div>,
        <div key='slide-3'>
            <AddtionalPreview additionalInfo={notes} contract={currentContract} />
        </div>,
        <div key='slide-4'>
            <PaymentPreview
                payments={payment as any}
                bartendingPackageTotal={totalBP}
                estimatedAlcoholPayment={
                    maxAlcohol + calculatePlatformFee(maxAlcohol, alcoholPlatformFee) +
                    calculatePlatformFee(
                        maxAlcohol,
                        stateTaxFee,
                    )
                }
                estimatedTotalCost={estimatedTotalCost}
                eventDate={eventDetails.eventDate}
            />
        </div>,
    ];
    const onToggle = () => setPreviewVisible(!previewVisible);

    const onSubmitFirst = async (
        data: QuoteForm,
        action: 'template' | 'draft' | 'updateTemplate',
        isGenerateLink?: boolean,
    ) => {
        const cocktailAndAlcohol = data?.cocktailItem?.map((item: any) => {
            // 5% platform fee is baked into price for cocktail type (Drinks & Alcohol)
            const pricePerItemCents = getCocktailPricePerItemCents(item);
            const pricePerItemDollars = pricePerItemCents / 100;
            return {
                provider: item?.provider?.value,
                cocktail_id: item?.cocktails && item?.cocktails?.id !== 'custom' ? item?.cocktails?.id : null,
                product_id: (!item?.cocktails || item?.cocktails?.id == 'custom') ? item?.brand?.id : item?.brand?.product?.id,
                brand_name: (!item?.cocktails || item?.cocktails?.id == 'custom') ? item?.brand?.name : item?.brand?.product?.name,
                drink_type: payloadDrinkType(item?.type?.type, item?.type?.value),
                cocktail_name: item?.cocktails?.name ?? null,
                type: item?.type?.value ?? null,
                price_per_item: convertDollarToCents(String(pricePerItemDollars)),
                quantity: item?.qty ?? 0,
                // brand_name: item?.brand && item?.brand?.id !== 'other' ? item?.brand?.product?.name : (item?.brandName ?? null),
                // product_id: item?.brand && item?.brand?.id !== 'other' ? item?.cocktails?.id === 'custom' ? item?.brand?.id : item?.brand?.product?.id : null,
                // price_per_item: item?.brand && item?.brand?.id !== 'other' ? item?.cocktails?.id === 'custom' ? Number(item?.brand?.price) / 100 : Number(item?.brand?.product?.price) / 100 : 1,
            };
        });
        const toPayloadNumber = (value: unknown): number => {
            const n = Number(value);
            return Number.isFinite(n) ? n : 0;
        };
        const bartenderPackage = data?.items?.map(({ id, ...rest }) => ({
            provider: rest.provider,
            name: rest.name,
            qty: rest.qty,
            cost_per_item: convertDollarToCents(String(toPayloadNumber(rest.costPerItem))),
            profit_per_item: convertDollarToCents(String(toPayloadNumber(rest.profitPerItem))),
            price_per_item: convertDollarToCents(String(toPayloadNumber(rest.pricePerItem))),
            line_total: convertDollarToCents(String(toPayloadNumber(rest.lineTotal))),
        }));

        let eventDate: Date | null = null;
        if (data?.event?.eventDate) {
            eventDate = new Date(data.event.eventDate);
        }

        const payload = {
            business_id: paramscurrBusiness?.id,
            store_id: '01988aae-0546-752a-a756-79d23b57dd14',
            quote_stage: action === 'draft' ? 'draft' : 'archived',
            quote_status: 'custom-quote',
            is_template: action === 'template' || action === 'updateTemplate' ? true : false,
            //event details
            address1: data.event.eventAddress,
            location: place?.lat && place?.lng ? {
                latitude: place?.lat,
                longitude: place?.lng
            } : null,
            city: place?.city || '',
            state: place?.state || '',
            postal_code: place?.zip || '',
            timezone: place?.timezone || '',
            ...((action === 'template' || action === 'updateTemplate') && { template_title: templateName }),
            guests: data.event.noOfGuests || 0,
            start_time: combineEventDateAndTimeLocal(eventDate, data.event.startTime) || null,
            end_time: combineEventDateAndTimeLocal(
                eventDate,
                data.event.endTime,
                isEndTimeNextDay(data.event.startTime, data.event.endTime),
            ) || null,
            drinker_type: data.event.drinker,
            event_type: data.event.eventType,
            event_site: data.event.eventLocation,
            type_of_service: data.event.typeOfService,
            additional_info: data.event.additionalInfo,
            //event details client details
            first_name: data.event.firstName,
            last_name: data.event.lastName,
            email: data.event.email,
            phone: data.event.phone,
            //bartender package calculations
            bartender_package: bartenderPackage,
            platform_fee: dollarsToCents(calculatePackagePlatformFee(subTotalBP, platformFee)),
            bartender_package_total: dollarsToCents(Number(subTotalBP)),
            service_total: dollarsToCents(Number(totalBP)),
            sub_total: 0,
            platform_fee_total: 0,
            //disount details
            discount_name: discount?.name,
            is_discount_percentage: Number(discount?.percentage) > 0 ? true : false,
            discount_applied: discount?.enabled,
            discount_total: Number(discount?.percentage) > 0 ?
                dollarsToCents((subTotalBP * Number(discount?.percentage)) / 100)
                : dollarsToCents(Number(discount?.amount)),
            // discount_total: discountAmountBP,
            // alcohol package calculations
            cocktails: cocktailAndAlcohol,
            estimated_alcohol_sub_total: dollarsToCents(Number(maxAlcohol || 0)),
            alcohol_platform_fee_total: dollarsToCents(calculatePlatformFee(maxAlcohol, alcoholPlatformFee)),
            estimated_alcohol_total:
                dollarsToCents(maxAlcohol + calculatePlatformFee(maxAlcohol, alcoholPlatformFee)),
            estimated_cost_total: 0,
            //additional info
            notes: notes,
            contract_id: currentContract?.id ?? null,
            //payment details
            is_full_payment: fullPayment || !isMoreThenSevenDays ? true : false,
            is_btc_provide: false,
            first_payment_date: null,
            first_payment_due_date: data.payment.firstPaymentDueDate,
            second_payment_date: null,
            second_payment_due_date:
                !fullPayment && isMoreThenSevenDays && eventDate
                    ? calendarDateToISODateUTC(
                        data.payment.secondPaymentDueDate ??
                        shiftCalendarDate(eventDate, -2),
                    )
                    : null,
            deposit: dollarsToCents(Number(data.payment.deposit || 0)),
            total_deposit: dollarsToCents(Number(data.payment.totalDeposit || 0)),
        };

        try {
            // console.log('payload----:', payload)
            setQuoteLoading(true);
            setSubmitAction(action);
            if (isGenerateLink) {
                setIsLinkLoading(true);
            }

            const isBlankQuote = !quoteIdToLoad;
            const isEditingExistingDraft = !!quoteIdToLoad && !isTemplateQuote;
            const isEditingExistingTemplate = !!quoteIdToLoad && isTemplateQuote;

            let quoteId: string | undefined;

            if (payload.first_name === undefined || payload.first_name.length < 0 || payload.first_name === '') {
                console.log("This is firstname provided issues text")
                throw new Error("First Name Must be provided")
            }

            // New quote + Generate Link only: call only quote-checkout API (no create quote first)
            if (isBlankQuote && isGenerateLink) {
                console.log("This is first if ")
                const { quote_stage, quote_status, is_template, ...restPayload } = payload;
                const checkoutPayload = {
                    ...restPayload,
                    is_quote_checkout: true,
                };
                const checkoutResp = await POST(createQuoteCheckout, checkoutPayload, refreshToken);
                quoteId = checkoutResp?.data?.quote_id;
                setQuote(quoteId ?? undefined);
                Alert({ message: checkoutResp?.data?.message });
                reset(data);
                setHasUserInteractedWithForm(false);
                setCheckoutLink(checkoutResp?.data?.client_facing_link ?? '');
                setIsLinkModalOpen(checkoutResp?.data?.client_facing_link ? true : false);
                if (quoteId) {
                    navigate(`/quoteBuilder/${quoteId}`, {
                        state: {
                            quote: { id: quoteId, is_template: false },
                            currBusiness: paramscurrBusiness,
                        },
                        replace: true,
                    });
                }
                return;
            }

            if ((isEditingExistingDraft && action === 'draft') || (isEditingExistingTemplate && action === 'updateTemplate')) {
                console.log("This is second if ")

                // UPDATE existing draft or template (we opened with this quote; save or generate link for it)
                quoteId = quoteIdToLoad;
                const resp = await PUT(`${createQuote}/${quoteId}`, payload, refreshToken);
                Alert({ message: resp?.data?.message });
                setQuote(quoteId);
                reset(data);
                setHasUserInteractedWithForm(false);
                if (action === 'updateTemplate') setIsCardModalOpen(false);
                if (!isGenerateLink) return;
                // else fall through to generate checkout link for this quoteId
            } else {
                console.log("This is first else ")

                // CREATE: blank quote (Save as Draft / Save as Template) or new from template
                const resp = await POST(createQuote, payload, refreshToken);
                quoteId = resp?.data?.quote_id;
                setQuote(quoteId ?? undefined);
                Alert({ message: resp?.data?.message });
                reset(data);
                setHasUserInteractedWithForm(false);
                if (action === 'template') setIsCardModalOpen(false);
                // Saving as a (new) template shouldn't navigate to the newly created
                // template — stay on whatever quote/template was already open.
                if (action === 'draft' && quoteId) {
                    navigate(`/quoteBuilder/${quoteId}`, {
                        state: {
                            quote: { id: quoteId, is_template: false },
                            currBusiness: paramscurrBusiness,
                        },
                        replace: true,
                    });
                }
            }

            // GENERATE checkout link (existing quote or draft updated above)
            if (isGenerateLink && quoteId) {
                console.log("This is third if ")

                const { quote_stage, quote_status, is_template, ...restPayload } = payload;
                const checkoutPayload = {
                    ...restPayload,
                    quote_id: quoteId,
                    is_quote_checkout: true,
                };
                const checkoutResp = await POST(createQuoteCheckout, checkoutPayload, refreshToken);
                // console.log('checkoutResp?.data?.data?.message-----:', checkoutResp?.data);
                Alert({ message: checkoutResp?.data?.message });
                setCheckoutLink(checkoutResp?.data?.client_facing_link ?? '');
                setIsLinkModalOpen(checkoutResp?.data?.client_facing_link ? true : false);
            }
        } catch (error: any) {
            Alert({
                type: 'error',
                message: error?.response?.data?.message || 'Something went wrong',
            });
        } finally {
            setQuoteLoading(false);
            setIsLinkLoading(false);
        }
    };
    const submitWithAction = (action: 'template' | 'draft' | 'updateTemplate', isGenerateLink?: boolean) => {
        handleSubmit(
            (data) => {

                if (!data.items || data.items.length === 0) {
                    Alert({ type: 'error', message: 'Please add at least one Bartender Package item.' });
                    return;
                } else if (!data.items.some(item => item.provider == 'companyProvide')) {
                    Alert({ type: 'error', message: 'Please add at least one Bartender Package item.' });
                    return;
                }
                onSubmitFirst(data, action, isGenerateLink);
            },
            (errors: any) => {
                const ev = errors.event || {};
                const errorMessage =
                    ev.noOfGuests?.message ||
                    ev.eventDate?.message ||
                    ev.startTime?.message ||
                    ev.endTime?.message ||
                    ev.drinker?.message ||
                    ev.firstName?.message ||
                    "Please fill in all required fields.";
                Alert({ type: 'error', message: errorMessage });
                console.log('FORM ERRORS:', errors);
            },
        )();
    };

    // const suggestedQuantity = useMemo(() => {
    //     return getProductQuantity(
    //         getValues('cocktail.type')?.value,
    //         Number(numberOfGuests),
    //         cocktailItem?.filter(
    //             (item) => item.cocktails?.value !== 'custom'
    //         ).length,
    //         Number(eventDetails.estDrinks),
    //     );
    // }, [
    //     cocktailItem,
    //     numberOfGuests,
    //     eventDetails.estDrinks,
    //     getValues('cocktail.brand')?.type,
    // ]);

    const handleLinkModal = async () => {
        // If a checkout link already exists, just open the modal and don't regenerate it
        if (checkoutLink) {
            setIsLinkModalOpen(true);
            return;
        }
        submitWithAction('draft', true);
    };

    const handleLinkCopy = () => {
        if (!checkoutLink) return;
        navigator.clipboard.writeText(checkoutLink);
        setLinkCopied(true);
    };

    return (
        <div className='pt-2'>
            {showLeaveModal && (
                <ChangesAlert
                    isOpen={showLeaveModal}
                    title="Unsaved Changes"
                    desc="Are you sure you want to leave without saving this quote?"
                    positiveButtonText="No, Stay"
                    negativeButtonText="Yes, Leave"
                    handleSaveChanges={() => setShowLeaveModal(false)}
                    handleDiscardChanges={() => {
                        dispatch(formStateChange(false));
                        setShowLeaveModal(false);
                        navigate('/events?tab=Leads');
                    }}
                />
            )}
            <div className='mx-auto w-full px-2'>
                <Header
                    previewVisible={previewVisible}
                    onPreviewPress={onToggle}
                    onBackClick={() => {
                        if (hasUserInteractedWithForm && isDirty) {
                            setShowLeaveModal(true);
                        } else {
                            navigate('/events?tab=Leads');
                        }
                    }}
                />
                <div className='mt-2 grid grid-cols-1 gap-8 lg:grid-cols-[60%_38%]'>
                    {/* Left: Accordions */}
                    <form
                        noValidate
                        onMouseDown={() => setHasUserInteractedWithForm(true)}
                        onKeyDown={() => setHasUserInteractedWithForm(true)}
                    >
                        <div
                            className={['w-full space-y-4', previewVisible ? 'hidden' : 'block', 'lg:block',].join(' ')}>
                            <Accordion
                                title={ACCORDION_TITLES.EVENT_DETAILS}
                                isOpen={openIndex === 0}
                                onToggle={() => toggleAt(0)}
                                content={
                                    <div>
                                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-12 mt-4'>
                                            <div className='w-full'>
                                                <p className='font-[500] text-[12px] mb-2 text-[#19191C] pl-2'>
                                                    Event Address
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='event.eventAddress'
                                                    // rules={{ required: "Event address is required" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <PlacesInput
                                                                defaultValue={
                                                                    getValues(
                                                                        'event.eventAddress',
                                                                    ) || undefined
                                                                }
                                                                onAddressSelected={
                                                                    onAddressSelected
                                                                }
                                                                classes='bg-[#F7F8FA] text-[#000000] font-[400] text-[14px] placeholder:text-[12px] placeholder:font-[400]'
                                                                error={
                                                                    errors.event?.eventAddress
                                                                        ?.message ?? ''
                                                                }
                                                                height={40}
                                                            />
                                                            {errors.event?.eventAddress && (
                                                                <p className='text-red-500 text-xs mt-1 ml-4'>
                                                                    {String(
                                                                        errors.event?.eventAddress
                                                                            .message,
                                                                    )}
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    Number of Guests
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='event.noOfGuests'
                                                    rules={{ required: "Please select a guest range" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <SearchSelect
                                                                options={GuestRanges}
                                                                isSearchable={false}
                                                                value={
                                                                    getValues('event.noOfGuests')
                                                                        ? {
                                                                            name: getGuestRangeFromValue(
                                                                                Number(getValues('event.noOfGuests')),
                                                                                1,
                                                                                25
                                                                            ),
                                                                            value: getValues('event.noOfGuests'),
                                                                        }
                                                                        : null
                                                                }
                                                                onChange={(selectedValue: any) => {
                                                                    field.onChange(
                                                                        selectedValue.value,
                                                                    );
                                                                }}
                                                                data-testid='search-id'
                                                                placeholder={'Number of Guests'}
                                                                height={40}
                                                            />
                                                            {errors.event?.noOfGuests && (
                                                                <p className='text-red-500 text-xs mt-1 ml-4'>
                                                                    {String(
                                                                        errors.event?.noOfGuests
                                                                            .message,
                                                                    )}
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                        </div>

                                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4'>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    Event Date
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='event.eventDate'
                                                    rules={{ required: "Select an event date" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <div className='flex flex-row items-center rounded-full bg-gray-50 focus-within:ring-1 focus-within:ring-red-500 focus-within:border-red-500'>
                                                                <DatePicker
                                                                    minDate={new Date()}
                                                                    dateFormat='MM/dd/yyyy'
                                                                    selected={field.value}
                                                                    onChange={(value: Date) => {
                                                                        field.onChange(
                                                                            value as Date,
                                                                        );
                                                                        // if (isMoreThan7DaysAway(value)) {
                                                                        //     setValue("payment.secondPaymentDueDate", value?.toISOString())
                                                                        // }
                                                                    }}
                                                                    placeholderText='Select Date'
                                                                    className='w-full px-4 h-[40px] text-[#000000] font-[400] text-[14px] placeholder:text-[12px] bg-gray-50 rounded-full outline-none'
                                                                    calendarClassName='rounded-xl p-2 z-50'
                                                                    popperClassName='z-50'
                                                                    popperPlacement='bottom-start'
                                                                    showPopperArrow={false}
                                                                    onKeyDown={(e) => e.preventDefault()}
                                                                />

                                                                <img
                                                                    src={calenderIcon}
                                                                    alt='Calendar Icon'
                                                                    className='mr-4'
                                                                />
                                                            </div>

                                                            {errors.event?.eventDate && (
                                                                <p className='text-red-500 text-xs mt-1 ml-4'>
                                                                    {String(
                                                                        errors.event?.eventDate
                                                                            .message,
                                                                    )}
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    Start Time
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='event.startTime'
                                                    rules={{ required: "Choose a start time" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <SearchSelect
                                                                options={TimeOptions}
                                                                isSearchable={false}
                                                                value={
                                                                    getValues(
                                                                        'event.startTime',
                                                                    ) && {
                                                                        name: getValues(
                                                                            'event.startTime',
                                                                        ),
                                                                        value: getValues(
                                                                            'event.startTime',
                                                                        ),
                                                                    }
                                                                }
                                                                onChange={(val) => {
                                                                    field.onChange(val.value);
                                                                }}
                                                                data-testid='search-id'
                                                                height={40}
                                                                placeholder={'Start Time'}
                                                            />
                                                            {errors.event?.startTime && (
                                                                <p className='text-red-500 text-xs mt-1 ml-4'>
                                                                    {String(
                                                                        errors.event?.startTime
                                                                            .message,
                                                                    )}
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    End Time
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='event.endTime'
                                                    rules={{ required: "Choose an end time", validate: ensureEndAtLeast30Min }}
                                                    render={({ field }) => (
                                                        <>
                                                            <SearchSelect
                                                                options={endOptions}
                                                                isSearchable={false}
                                                                value={
                                                                    field.value && {
                                                                        name: field.value,
                                                                        value: field.value,
                                                                    }
                                                                }
                                                                onChange={(val) => {
                                                                    field.onChange(val?.value ?? val?.id ?? '');
                                                                    trigger('event.endTime');
                                                                }}
                                                                data-testid='search-id'
                                                                height={40}
                                                                placeholder={startTime ? 'End Time' : 'Select start time first'}
                                                            />
                                                            {startTime &&
                                                                endOptions.length === 0 && (
                                                                    <p className='text-amber-600 text-xs mt-1'>
                                                                        No valid end times left
                                                                        today. Choose an earlier
                                                                        start time.
                                                                    </p>
                                                                )}
                                                            {errors.event?.endTime && (
                                                                <p className='text-red-500 text-xs mt-1 ml-4'>
                                                                    {String(
                                                                        errors.event?.endTime
                                                                            .message,
                                                                    )}
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                        </div>

                                        {/* Drinking Pace picker hidden; drinker defaults to 'light' and estDrinks still needs to be kept in sync */}
                                        <Controller
                                            control={control}
                                            name='event.drinker'
                                            render={() => <></>}
                                        />

                                        {/* hidden controller to validate estDrinks conditionally */}
                                        <Controller
                                            control={control}
                                            name='event.estDrinks'
                                            // rules={{
                                            //     validate: (v) => {
                                            //         if (!drinkerWatch) return true;
                                            //         if (v === "" || Number.isNaN(Number(v))) return "Estimated drinks is required";
                                            //         if (Number(v) <= 0) return "Estimated drinks must be greater than 0";
                                            //         return true;
                                            //     }
                                            // }}
                                            render={() => <></>}
                                        />

                                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4'>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    Event Type
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='event.eventType'
                                                    // rules={{ required: "Select an event type" }}
                                                    render={({ field }) => {
                                                        return (
                                                            <>
                                                                <SearchSelect
                                                                    isSearchable={false}
                                                                    options={EventTypes}
                                                                    value={
                                                                        field.value && {
                                                                            name: EVENT_TYPE_MAP[field.value]?.name || '',
                                                                            value: field.value,
                                                                        }
                                                                    }
                                                                    onChange={(val) =>
                                                                        field.onChange(val.value)
                                                                    }
                                                                    data-testid='search-id'
                                                                    height={40}
                                                                    placeholder={'Event Type'}
                                                                />
                                                                {errors.event?.eventType && (
                                                                    <p className='text-red-500 text-xs mt-1 ml-4'>
                                                                        {String(
                                                                            errors.event?.eventType
                                                                                .message,
                                                                        )}
                                                                    </p>
                                                                )}
                                                            </>
                                                        )
                                                    }}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    Indoor / Outdoor
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='event.eventLocation'
                                                    // rules={{ required: "Select a location type" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <SearchSelect
                                                                options={EventLocation}
                                                                isSearchable={false}
                                                                value={
                                                                    field.value && {
                                                                        name: field.value,
                                                                        value: field.value,
                                                                    }
                                                                }
                                                                onChange={(val) =>
                                                                    field.onChange(val.value)
                                                                }
                                                                data-testid='search-id'
                                                                placeholder={'Indoor / Outdoor'}
                                                                height={40}
                                                            />
                                                            {errors.event?.eventLocation && (
                                                                <p className='text-red-500 text-xs mt-1 ml-4'>
                                                                    {String(
                                                                        errors.event?.eventLocation
                                                                            .message,
                                                                    )}
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    Type of Service
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='event.typeOfService'
                                                    render={({ field }) => (
                                                        <>
                                                            <SearchSelect
                                                                options={TypeOfServiceOptions}
                                                                isSearchable={false}
                                                                value={
                                                                    field.value
                                                                        ? TypeOfServiceOptions.find(
                                                                            (o) => o.value === field.value,
                                                                        ) ?? null
                                                                        : null
                                                                }
                                                                onChange={(val) =>
                                                                    field.onChange(val.value)
                                                                }
                                                                placeholder={'Type of Service'}
                                                                height={40}
                                                            />
                                                            {errors.event?.typeOfService && (
                                                                <p className='text-red-500 text-xs mt-1 ml-4'>
                                                                    {String(
                                                                        errors.event?.typeOfService
                                                                            .message,
                                                                    )}
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div className=' h-[1px] bg-gray-200 mt-3' />
                                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4'>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    Client First Name
                                                </p>
                                                <input
                                                    type='text'
                                                    maxLength={21}
                                                    className={`w-full px-4 py-2 h-[40px] bg-p-gray rounded-full text-[#000000] font-[400] text-[14px] placeholder:text-[12px] focus:outline-none  focus:ring-1 focus:ring-primary-red ${errors.event?.firstName
                                                        ? 'ring-1 ring-red-500'
                                                        : ''
                                                        }`}
                                                    placeholder={'Client First Name'}
                                                    {...register('event.firstName', {
                                                        required: 'First name is required',
                                                        validate: (value) =>
                                                            (typeof value === 'string' && value.trim().length > 0) ||
                                                            'First name is required',
                                                    })}
                                                />
                                                {errors.event?.firstName && (
                                                    <p className='text-red-500 text-xs mt-1 ml-4'>
                                                        {String(errors.event?.firstName.message)}
                                                    </p>
                                                )}
                                            </div>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    Client Last Name
                                                </p>
                                                <input
                                                    type='text'
                                                    maxLength={21}
                                                    className={`w-full px-4 py-2 bg-p-gray rounded-full text-[#000000] font-[400] text-[14px] h-[40px] text-xs placeholder:text-[12px] focus:outline-none focus:ring-1 focus:ring-primary-red ${errors.event?.lastName
                                                        ? 'ring-1 ring-red-500'
                                                        : ''
                                                        }`}
                                                    placeholder={'Client Last Name'}
                                                    {...register(
                                                        'event.lastName',
                                                        // { required: "Last name is required" }
                                                    )}
                                                />
                                                {errors.event?.lastName && (
                                                    <p className='text-red-500 text-xs mt-1 ml-4'>
                                                        {String(errors.event?.lastName.message)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4'>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    Client Email
                                                </p>
                                                <input
                                                    type='email'
                                                    className={`w-full px-4 py-2 h-[40px] bg-p-gray rounded-full text-[#000000] font-[400] text-[14px] placeholder:text-[12px] focus:outline-none  focus:ring-1 focus:ring-primary-red ${errors.event?.email
                                                        ? 'ring-1 ring-red-500'
                                                        : ''
                                                        }`}
                                                    placeholder={'Client Email Address'}
                                                    {...register('event.email', {
                                                        // required: "Email is required",
                                                        pattern: {
                                                            value: /\b[\w.-]+@[\w.-]+\.[A-Za-z]{2,}\b/,
                                                            message: 'Enter a valid email',
                                                        },
                                                    })}
                                                />
                                                {errors.event?.email && (
                                                    <p className='text-red-500 text-xs mt-1 ml-4'>
                                                        {String(errors.event?.email.message)}
                                                    </p>
                                                )}
                                            </div>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                    Client Phone Number
                                                </p>
                                                <input
                                                    type='tel'
                                                    maxLength={30}
                                                    className={`w-full px-4 py-2 h-[40px] text-[#000000] font-[400] text-[14px] bg-p-gray rounded-full text-xs placeholder:text-[12px] focus:outline-none focus:ring-1 focus:ring-primary-red${errors.event?.phone
                                                        ? 'ring-1 ring-red-500'
                                                        : ''
                                                        }`}
                                                    placeholder={'Client Phone Number'}
                                                    {...register('event.phone', {
                                                        validate: (value: string) => {
                                                            if (!value || value.trim() === '') return true;
                                                            const tel = value.replace(/[^0-9]/g, '');
                                                            if (/^1?\d{10}$/.test(tel)) return true;
                                                            return 'Enter a valid phone number';
                                                        },
                                                    })}
                                                />
                                                {errors.event?.phone && (
                                                    <p className='text-red-500 text-xs mt-1 ml-4'>
                                                        {String(errors.event?.phone.message)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className='w-full mt-4'>
                                            <p className='font-semibold text-xs mb-2 text-[#19191C] pl-2'>
                                                Additional Information
                                            </p>
                                            <textarea
                                                {...register('event.additionalInfo')}
                                                placeholder='Write additional information here...'
                                                rows={4}
                                                className='w-full resize-none rounded-2xl bg-[#f7f8fa] p-4 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-orange-500 placeholder-gray-400'
                                            />
                                        </div>
                                    </div>
                                }
                            />
                            {/* Remaining accordions unchanged */}
                            <Accordion
                                title={ACCORDION_TITLES.PACKAGE_AND_ADD_ONS}
                                isOpen={openIndex === 1}
                                onToggle={() => toggleAt(1)}
                                content={
                                    <PackageDetailsCard
                                        control={control}
                                        register={register}
                                        getValues={getValues}
                                        setValue={setValue}
                                        draft={draft}
                                        items={fields.map((f) => toQuoteItem(f as unknown as ItemLine))}
                                        currency={currency}
                                        calculatorPackages={calculatorPackages}
                                        calcPackage={calcPackage}
                                        calcPackageTotals={calcPackageTotals}
                                        includePortableBar={includePortableBar}
                                        onIncludePortableBarChange={setIncludePortableBar}
                                        includeIceAndCooler={includeIceAndCooler}
                                        onIncludeIceAndCoolerChange={setIncludeIceAndCooler}
                                        travelCost={travelCost}
                                        eventDistance={eventDistance}
                                        includeTravelCost={includeTravelCost}
                                        onIncludeTravelCostChange={setIncludeTravelCost}
                                        includeGratuity={includeGratuity}
                                        onIncludeGratuityChange={setIncludeGratuity}
                                        onCalcPackageChange={setCalcPackageId}
                                        templateSelect={renderTemplateSelect(
                                            selectedPackageTemplateId,
                                            handleSelectPackageTemplate,
                                            applyingPackageTemplate,
                                            'w-full sm:w-[calc((100%-4rem)/3)]',
                                        )}
                                        onAddClick={handleBPAddClick}
                                        onEdit={handleBPEdit}
                                        onRemove={handleBPRemove}
                                        onCancelEdit={() =>
                                            setValue('draft', {
                                                id: '',
                                                provider: '',
                                                name: '',
                                                qty: '',
                                                costPerItem: '',
                                                profitPerItem: '',
                                                pricePerItem: '',
                                                lineTotal: '',
                                                editingIndex: null,
                                            })
                                        }
                                        platformFeeAmount={platformFeeAmount}
                                        yourFee={yourFee}
                                        subTotalBP={subTotalBP}
                                        afterPlatformFee={afterPlatformFee}
                                    />
                                }
                            />
                            <Accordion
                                title={ACCORDION_TITLES.DRINKS_AND_ALCOHOL}
                                titleSuffix={
                                    // Match Type of Drinker pills (Drinker.tsx): same asset, size, and popover styling
                                    <Popover className="relative mt-1">
                                        <PopoverButton
                                            type="button"
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                            className="outline-none"
                                            aria-label="More info"
                                        >
                                            <img src={infoIcon} className="w-3 h-3" alt="" />
                                        </PopoverButton>
                                        <PopoverPanel
                                            anchor={{ to: 'bottom start', gap: 4 }}
                                            className="z-50 w-72 whitespace-normal break-words rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-lg"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Drink quantities are auto-calculated based on event hours, drinking pace, and guest count. Cocktails are prioritized, wine and beer share the rest, sparkling adds 5%, and water is included separately.
                                        </PopoverPanel>
                                    </Popover>
                                }
                                isOpen={openIndex === 2}
                                onToggle={() => toggleAt(2)}
                                content={
                                    <div>
                                        <p className='font-semibold text-gray-800 text-base flex justify-end gap-1'>
                                            Est Drinks:{' '}
                                            <span className='text-primary-red font-bold'>
                                                {eventDetails.estDrinks}
                                            </span>
                                        </p>
                                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4'>
                                            <div className='w-full'>
                                                <p className='font-semibold  text-xs mb-2 pl-2'>
                                                    Provided By
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='cocktail.provider'
                                                    render={({ field }) => {
                                                        return (
                                                            <SearchSelect
                                                                isSearchable={false}
                                                                options={
                                                                    paramscurrBusiness?.is_standard_package
                                                                        ? CocktailProviders
                                                                        : CocktailProviders.filter(p => p.value !== 'store-provide' && p.value !== 'ho-provide')
                                                                }
                                                                value={
                                                                    getValues(
                                                                        'cocktail.provider',
                                                                    ) && {
                                                                        value: getValues(
                                                                            'cocktail.provider',
                                                                        )?.id,
                                                                        name: getValues(
                                                                            'cocktail.provider',
                                                                        )?.name,
                                                                    }
                                                                }
                                                                onChange={(selectedValue: any) => {
                                                                    field.onChange(selectedValue);
                                                                }}
                                                                data-testid='search-id'
                                                                placeholder={'Provides'}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2 pl-2'>
                                                    Type
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='cocktail.type'
                                                    render={({ field }) => {
                                                        return (
                                                            <SearchSelect
                                                                options={packageCocktailType}
                                                                value={
                                                                    getValues('cocktail.type') && {
                                                                        value: getValues(
                                                                            'cocktail.type',
                                                                        )?.type,
                                                                        name: getValues(
                                                                            'cocktail.type',
                                                                        )?.name,
                                                                    }
                                                                }
                                                                onChange={(selectedValue: any) => {
                                                                    field.onChange(selectedValue);
                                                                    setValue(
                                                                        'cocktail.cocktails',
                                                                        null,
                                                                    );
                                                                    setValue(
                                                                        'cocktail.brand',
                                                                        null,
                                                                    );
                                                                }}
                                                                data-testid='search-id'
                                                                placeholder={'Type'}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </div>
                                            <div
                                                className={`w-full ${cocktailSelectedType?.type == 'products' || cocktail?.type?.value == 'other' ? 'hidden' : ''}`}
                                            >
                                                <p className='font-semibold text-xs mb-2  pl-2'>
                                                    Cocktails
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='cocktail.cocktails'
                                                    render={({ field }) => {
                                                        return (
                                                            <SearchSelect
                                                                isLoading={typeLoading}
                                                                options={packageCocktails}
                                                                value={
                                                                    getValues(
                                                                        'cocktail.cocktails',
                                                                    ) && {
                                                                        value: getValues(
                                                                            'cocktail.cocktails',
                                                                        )?.id,
                                                                        name: getValues(
                                                                            'cocktail.cocktails',
                                                                        )?.name,
                                                                    }
                                                                }
                                                                onChange={(selectedValue: any) => {
                                                                    field.onChange(selectedValue);
                                                                    const isCustom =
                                                                        selectedValue?.id === 'custom' ||
                                                                        selectedValue?.value === 'custom';
                                                                    if (isCustom) {
                                                                        setValue('cocktail.brand', null);
                                                                    } else {
                                                                        const ingredients = Array.isArray(
                                                                            selectedValue?.cocktail_ingredients,
                                                                        )
                                                                            ? selectedValue.cocktail_ingredients
                                                                            : [];
                                                                        const firstBrand = ingredients.find(
                                                                            (ing: any) =>
                                                                                ing?.product &&
                                                                                ing?.product?.type !== 'mixers',
                                                                        );
                                                                        setValue(
                                                                            'cocktail.brand',
                                                                            firstBrand ?? null,
                                                                        );
                                                                    }
                                                                }}
                                                                data-testid='search-id'
                                                                placeholder={'Cocktail'}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </div>
                                            <div
                                                className={`w-full ${cocktail?.type?.value == 'other'
                                                    ? 'hidden'
                                                    : cocktail?.type?.type === 'cocktails' &&
                                                        cocktail?.cocktails &&
                                                        cocktail?.cocktails?.id !== 'custom' &&
                                                        cocktail?.cocktails?.value !== 'custom'
                                                        ? 'hidden'
                                                        : ''
                                                    }`}
                                            >
                                                <p className='font-semibold text-xs mb-2 pl-2'>
                                                    Brand
                                                </p>
                                                <Controller
                                                    name='cocktail.brand'
                                                    control={control}
                                                    render={({ field }) => {
                                                        return (
                                                            <SearchSelect
                                                                options={storeBrand}
                                                                value={
                                                                    getValues('cocktail.brand') && {
                                                                        value: getValues(
                                                                            'cocktail.brand',
                                                                        )?.id,
                                                                        name: getValues(
                                                                            'cocktail.brand',
                                                                        )?.name,
                                                                    }
                                                                }
                                                                onChange={(selectedValue: any) => {
                                                                    field.onChange(selectedValue);
                                                                }}
                                                                isLoading={brandLoading}
                                                                data-testid='search-id'
                                                                placeholder={'Brand'}
                                                                preferedProduct={preferredProducts}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </div>
                                            <div
                                                className={`w-full ${cocktail?.type?.value == 'other' || cocktail?.brand?.id == 'other' ? 'visible' : 'hidden'}`}
                                            >
                                                <p className='font-semibold text-xs mb-2 pl-2'>
                                                    Brand Name
                                                </p>
                                                <input
                                                    type='text'
                                                    maxLength={21}
                                                    className='w-full px-4 py-2 h-[40px] text-[#000000] font-[400] text-[14px] placeholder:text-[12px] bg-p-gray rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-primary-red'
                                                    placeholder={'Brand Name'}
                                                    {...register('cocktail.brandName')}
                                                />
                                            </div>
                                        </div>
                                        <div className='w-full mt-4'>
                                            <CocktailItem
                                                items={cocktailFields}
                                                onAddClick={handleCocktailAddClick}
                                                onEdit={handleCocktailEdit}
                                                onRemove={handleCocktailRemove}
                                                onRemoveGroup={handleCocktailRemoveGroup}
                                                onQtyChange={handleCocktailQtyChange}
                                                numberOfGuests={Number(numberOfGuests)}
                                                estDrinks={Number(eventDetails?.estDrinks)}
                                                editIndex={cocktail?.editingIndex}
                                                isAddDisabled={isCocktailSaveDisabled}
                                                preferredProducts={preferredProducts}
                                                onCancelEdit={() =>
                                                    setValue('cocktail', {
                                                        id: '',
                                                        provider: '',
                                                        qty: '',
                                                        type: '',
                                                        cocktails: '',
                                                        brand: '',
                                                        brandName: '',
                                                        pricePerItem: '',
                                                        editingIndex: null,
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className='flex mt-10 justify-between gap-4 flex-wrap'>
                                            <div className='w-full sm:max-w-xs'>
                                                <div className='flex w-full items-center justify-between gap-3 rounded-2xl border border-primary-red bg-[rgb(255,238,231)] px-5 py-3'>
                                                    <div className='flex items-center gap-1.5'>
                                                        <p className='text-sm font-semibold text-[#19191C]'>Points</p>
                                                        <Popover className='relative flex items-center'>
                                                            <PopoverButton
                                                                type='button'
                                                                onClick={(e) => e.stopPropagation()}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onKeyDown={(e) => e.stopPropagation()}
                                                                className='flex items-center outline-none'
                                                                aria-label='More info'
                                                            >
                                                                <img
                                                                    src={infoIcon}
                                                                    alt=''
                                                                    className='h-3.5 w-3.5 opacity-70'
                                                                />
                                                            </PopoverButton>
                                                            <PopoverPanel
                                                                anchor={{ to: 'bottom start', gap: 4 }}
                                                                className='z-50 w-60 whitespace-normal break-words rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-lg'
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                These reward points represent 5% bonus earned from Preferred Brand sales.
                                                            </PopoverPanel>
                                                        </Popover>
                                                    </div>
                                                    <p className='text-sm font-semibold text-[#19191C]'>
                                                        {Math.ceil(preferredBrandBonus)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className='w-full sm:max-w-xs'>
                                                <MaxAlcoholCard amount={maxAlcohol} />
                                            </div>
                                        </div>
                                    </div>
                                }
                            />
                            <Accordion
                                title={ACCORDION_TITLES.TERMS_AND_ADDITIONAL_DETAILS}
                                isOpen={openIndex === 3}
                                onToggle={() => toggleAt(3)}
                                content={
                                    <>
                                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4 overflow-visible z-[50]'>
                                            <div className='w-full md:w-1/2'>
                                                <div className='w-full'>
                                                    <p className='font-semibold text-xs mb-2  pl-2'>
                                                        Contract
                                                    </p>
                                                    <SearchSelect
                                                        options={contracts}
                                                        value={
                                                            currentContract ? {
                                                                value: currentContract?.id,
                                                                name: currentContract?.name,
                                                            } : null}
                                                        onChange={(selectedValue) => {
                                                            setCurrentContract(selectedValue ?? undefined);
                                                        }}
                                                        data-testid='search-id'
                                                        placeholder={'Select Contract'}
                                                        isClearable
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4'>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2  pl-2'>
                                                    Notes
                                                </p>
                                                <textarea
                                                    value={notes}
                                                    onChange={(e) => setNotes(e.target.value)}
                                                    placeholder='Write your notes here...'
                                                    rows={4}
                                                    className='w-full resize-none rounded-2xl bg-[#f7f8fa] p-4 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-orange-500 placeholder-gray-400'
                                                />
                                            </div>
                                        </div>
                                    </>
                                }
                            />
                            <Accordion
                                title={ACCORDION_TITLES.PAYMENT_SCHEDULE}
                                isOpen={openIndex === 4}
                                onToggle={() => toggleAt(4)}
                                content={
                                    <div>
                                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4'>
                                            <div className='flex-1 w-full'>
                                                <div className=''>
                                                    <div className='flex gap-4 justify-between'>
                                                        <div className='flex flex-row gap-2'>
                                                            <p className='text-xs mb-2 font-[500] text-black'>
                                                                Want to Pay in full?
                                                            </p>
                                                            <div>
                                                                <ToggleInput
                                                                    disabled={!isMoreThenSevenDays}
                                                                    checked={fullPayment}
                                                                    onChange={(val: any) => {
                                                                        setValue(
                                                                            'payment.fullPayment',
                                                                            val.target.checked,
                                                                        );
                                                                        setValue(
                                                                            'payment.deposit',
                                                                            discountedSubTotalBP,
                                                                            {
                                                                                shouldValidate: true,
                                                                                shouldDirty: false,
                                                                            },
                                                                        );
                                                                        setValue(
                                                                            'payment.totalDeposit',
                                                                            discountedSubTotalBP,
                                                                            // +
                                                                            // platformFee,
                                                                            {
                                                                                shouldValidate: true,
                                                                                shouldDirty: false,
                                                                            },
                                                                        );
                                                                        setValue(
                                                                            'payment.secondPayment',
                                                                            nnum(''),
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <p className='text-sm font-[400] mb-2 ml-2  pl-2 text-[#000000]'>
                                                            Service Total:{' '}
                                                            <span className='text-primary-red font-bold'>
                                                                {totalBP > 0 && fmt(totalBP)}
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className=' h-[1px] bg-gray-200 mt-3' />
                                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4'>
                                            <div className='w-full'>
                                                <p className='font-semibold  text-xs mb-2  pl-2'>
                                                    Deposit
                                                </p>
                                                <input
                                                    type='text'
                                                    maxLength={21}
                                                    max={Number(discountedSubTotalBP)}
                                                    className='w-full px-4 py-2 h-[40px] text-[#000000] font-[400] text-[14px] placeholder:text-[12px] bg-p-gray rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-primary-red'
                                                    placeholder={'Deposit'}
                                                    step='0.01'
                                                    onInput={(e) => {
                                                        const input = e.currentTarget;
                                                        // Strip more than 2 decimal places
                                                        input.value = input.value.replace(/(\.\d{2})\d+/, '$1');
                                                        const value = Number(input.value);
                                                        if (value > discountedSubTotalBP) {
                                                            input.value = String(discountedSubTotalBP);
                                                        }
                                                    }}
                                                    // value={subTotalBP > 0 ? subTotalBP : ''}
                                                    disabled={fullPayment || !isMoreThenSevenDays}
                                                    {...register('payment.deposit', {
                                                        valueAsNumber: true,
                                                        min: {
                                                            value: minDeposit,
                                                            message: `Deposit cannot be less than ${minDeposit.toFixed(2)}`,
                                                        },
                                                        max: {
                                                            value: discountedSubTotalBP,
                                                            message: `Deposit cannot be greater than ${discountedSubTotalBP.toFixed(2)}`,
                                                        },
                                                    })}
                                                />
                                                {errors.payment?.deposit && (
                                                    <p className='text-red-500 text-xs mt-1 ml-4'>
                                                        {String(errors.payment?.deposit.message)}
                                                    </p>
                                                )}
                                            </div>
                                            {/* <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2  pl-2'>
                                                    Platform Fee
                                                </p>
                                                <input
                                                    type='text'
                                                    maxLength={21}
                                                    disabled
                                                    value={calculatePlatformFee(
                                                        subTotalBP,
                                                        platformFee,
                                                    )}
                                                    className='w-full px-4 py-2 h-[40px] text-[#000000] font-[400] text-[14px] bg-p-gray rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-primary-red '
                                                    placeholder={'Platform Fee'}
                                                    {...register('payment.platformFee')}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2  pl-2'>
                                                    Total Deposit
                                                </p>
                                                <input
                                                    type='text'
                                                    disabled
                                                    // value={Number(paymentDeposit) + platformFee}
                                                    maxLength={21}
                                                    className='w-full px-4 py-2 h-[40px] text-[#000000] font-[400] text-[14px] bg-p-gray rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-primary-red '
                                                    placeholder={'Total Deposit'}
                                                    {...register('payment.totalDeposit')}
                                                />
                                            </div> */}
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2  pl-2'>
                                                    {fullPayment ? 'Payment Due Date' : 'Service Deposit Due Date'}
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='payment.firstPaymentDueDate'
                                                    // rules={{ required: "Select an event date" }}
                                                    render={({ field }) => (
                                                        <>
                                                            <DatePicker
                                                                disabled={eventDetails.eventDate ? false : true}
                                                                minDate={new Date()}
                                                                maxDate={firstPaymentDueDateMax ?? undefined}
                                                                dateFormat='MM/dd/yyyy'
                                                                selected={field.value}
                                                                onChange={(value) =>
                                                                    field.onChange(
                                                                        value as Date | null,
                                                                    )
                                                                }
                                                                onKeyDown={(e) => e.preventDefault()}
                                                                placeholderText='Select Date'
                                                                className='w-full px-4 h-[40px] text-[#000000] font-[400] text-[14px] placeholder:text-[12px] bg-gray-50 rounded-full outline-none'
                                                                calendarClassName='rounded-xl p-2 z-50'
                                                                popperClassName='z-50'
                                                                popperPlacement='bottom-start'
                                                                showPopperArrow={false}
                                                            />
                                                            {errors.payment
                                                                ?.firstPaymentDueDate && (
                                                                    <p className='text-red-500 text-xs mt-1 ml-4'>
                                                                        {String(
                                                                            errors.payment
                                                                                ?.firstPaymentDueDate
                                                                                .message,
                                                                        )}
                                                                    </p>
                                                                )}
                                                        </>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className={`flex flex-col gap-4 sm:flex-row sm:gap-8 mt-4  ${fullPayment || !isMoreThenSevenDays ? 'hidden' : ''}`}
                                        >
                                            <div className='w-full'>
                                                <p className='font-semibold  text-xs mb-2  pl-2'>
                                                    2nd Payment
                                                </p>
                                                <input
                                                    disabled
                                                    type='text'
                                                    maxLength={21}
                                                    className='w-full px-4 py-3 bg-p-gray rounded-full focus:outline-none  focus:ring-1 focus:ring-primary-red text-gray-600 placeholder:text-[12px]'
                                                    placeholder={'2nd Payment'}
                                                    {...register('payment.secondPayment')}
                                                />
                                            </div>
                                            <div className='w-full'>
                                                <p className='font-semibold text-xs mb-2  pl-2'>
                                                    Remaining Service Balance Due Date
                                                </p>
                                                <Controller
                                                    control={control}
                                                    name='payment.secondPaymentDueDate'
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            minDate={shiftCalendarDate(new Date(), 1)}
                                                            maxDate={secondPaymentDueDateMax ?? undefined}
                                                            dateFormat='MM/dd/yyyy'
                                                            selected={field.value}
                                                            onChange={(value) =>
                                                                field.onChange(
                                                                    value as Date | null,
                                                                )
                                                            }
                                                            placeholderText='Select Date'
                                                            className='w-full px-4 h-[40px] text-[#000000] font-[400] text-[14px] placeholder:text-[12px] bg-gray-50 rounded-full outline-none'
                                                            calendarClassName='rounded-xl p-2 z-50'
                                                            popperClassName='z-50'
                                                            popperPlacement='bottom-start'
                                                            showPopperArrow={false}
                                                            onKeyDown={(e) => e.preventDefault()}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div className='mt-4 mb-4 flex w-full flex-col sm:flex-row sm:justify-end gap-3'>
                                            <div className='w-full sm:w-1/2 sm:max-w-md self-end'>
                                                <div className='flex w-full items-center justify-between gap-3 rounded-2xl border border-primary-red bg-[rgb(255,238,231)] px-5 py-3'>
                                                    <div className='flex items-center gap-1.5'>
                                                        <p className='text-sm font-semibold text-[#19191C]'>Total Points</p>
                                                        <Popover className='relative flex items-center'>
                                                            <PopoverButton
                                                                type='button'
                                                                onClick={(e) => e.stopPropagation()}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onKeyDown={(e) => e.stopPropagation()}
                                                                className='flex items-center outline-none'
                                                                aria-label='More info'
                                                            >
                                                                <img
                                                                    src={infoIcon}
                                                                    alt=''
                                                                    className='h-3.5 w-3.5 opacity-70'
                                                                />
                                                            </PopoverButton>
                                                            <PopoverPanel
                                                                anchor={{ to: 'bottom start', gap: 4 }}
                                                                className='z-50 w-60 whitespace-normal break-words rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-lg'
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                These reward points are the combined total of platform fee and 5% Preferred Brand bonus.
                                                            </PopoverPanel>
                                                        </Popover>
                                                    </div>
                                                    <p className='text-sm font-semibold text-[#19191C]'>
                                                        {Math.ceil(platformFeeAmount) + Math.ceil(preferredBrandBonus)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className='w-full sm:w-1/2 sm:max-w-md'>
                                                <CostSummaryCard
                                                    BPTotal={totalBP}
                                                    estimatedAlcohol={
                                                        maxAlcohol
                                                        +
                                                        calculatePlatformFee(
                                                            maxAlcohol,
                                                            alcoholPlatformFee,
                                                        )
                                                        +
                                                        calculatePlatformFee(
                                                            maxAlcohol,
                                                            stateTaxFee,
                                                        )
                                                    }
                                                    estimatedlTotalCost={estimatedTotalCost}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                }
                            />
                            <div className='flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 justify-center sm:justify-end w-full'>
                                {quoteIdToLoad && !isTemplateQuote ? (
                                    <div className='flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 w-full sm:w-auto'>
                                        <Button
                                            type='button'
                                            disabled={quoteLoading}
                                            classes='bg-black text-white flex items-center justify-center gap-2 px-4 py-2 w-full sm:w-auto min-w-0'
                                            onClick={() => {
                                                setTemplateName('');
                                                setIsCardModalOpen(true);
                                            }}
                                        >
                                            {quoteLoading && submitAction === 'template'
                                                ? 'Saving...'
                                                : 'Save Template'}
                                        </Button>
                                        <Button
                                            disabled={quoteLoading}
                                            type='button'
                                            classes='bg-primary-red text-white flex items-center justify-center gap-2 px-4 py-2 w-full sm:w-auto min-w-0'
                                            onClick={() => {
                                                submitWithAction('draft');
                                            }}
                                        >
                                            {quoteLoading && submitAction === 'draft'
                                                ? 'Saving...'
                                                : 'Save'}
                                        </Button>
                                    </div>
                                ) : (
                                    <div className='flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 w-full sm:w-auto'>
                                        {!isTemplateQuote && (
                                            <Button
                                                type='button'
                                                disabled={quoteLoading}
                                                classes='bg-black text-white flex items-center justify-center gap-2 px-4 py-2 w-full sm:w-auto min-w-0'
                                                onClick={() => setIsCardModalOpen(true)}
                                            >
                                                {quoteLoading && submitAction == 'template'
                                                    ? 'Saving...'
                                                    : 'Save Template'}
                                            </Button>
                                        )}
                                        {isTemplateQuote && quoteIdToLoad && (
                                            <Button
                                                type='button'
                                                disabled={quoteLoading}
                                                classes='bg-black text-white flex items-center justify-center gap-2 px-4 py-2 w-full sm:w-auto min-w-0'
                                                onClick={() => {
                                                    setIsCardModalOpen(true);
                                                }}
                                            >
                                                {quoteLoading && submitAction == 'updateTemplate'
                                                    ? 'Updating...'
                                                    : 'Update Template'}
                                            </Button>
                                        )}
                                        <Button
                                            disabled={quoteLoading}
                                            type='button'
                                            classes='bg-primary-red text-white flex items-center justify-center gap-2 px-4 py-2 w-full sm:w-auto min-w-0'
                                            onClick={() => {
                                                submitWithAction('draft');
                                            }}
                                        >
                                            {quoteLoading && submitAction == 'draft'
                                                ? 'Saving...'
                                                : 'Save as Draft'}
                                        </Button>
                                    </div>
                                )}
                                <Button
                                    onClick={() => handleLinkModal()}
                                    type='button'
                                    disabled={isLinkLoading}
                                    classes='border border-black text-black flex items-center justify-center gap-2 px-4 py-2'
                                >
                                    <img src={shareIcon} className='brightness-0 h-4 w-4' alt='Share' />
                                    <span>
                                        {isLinkLoading
                                            ? 'Generating Link...'
                                            : checkoutLink
                                                ? 'Open Link'
                                                : 'Generate Link'}
                                    </span>
                                </Button>
                                {quoteIdToLoad && !isTemplateQuote && (
                                    <Button
                                        onClick={() => downloadQuotePdf(quote)}
                                        type='button'
                                        disabled={downloadLoading}
                                        classes='bg-black text-white flex items-center justify-center gap-2 px-4 py-2'
                                    >
                                        <img src={downloadIcon} className='h-4 w-4' alt='Download' />
                                        <span>{downloadLoading ? 'Downloading...' : 'Download'}</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                        <div id="pdf" style={{ display: "none" }}></div>
                        <SaveTemplateModal
                            isOpen={isCardModalOpen}
                            onClose={closeCardModal}
                            templateName={templateName}
                            onTemplateNameChange={setTemplateName}
                            onSave={() => submitWithAction('template')}
                            showUpdateOption={isTemplateQuote && !!quoteIdToLoad}
                            onUpdate={() => submitWithAction('updateTemplate')}
                            existingTemplates={templateOptions.map((t) => ({ id: t?.id, title: t?.template_title }))}
                            currentTemplateId={quoteIdToLoad}
                            isExistingTemplatesLoading={templateOptionsLoading}
                            isSaving={quoteLoading && submitAction === 'template'}
                            isUpdating={quoteLoading && submitAction === 'updateTemplate'}
                        />
                        <GeneratedLinkModal
                            isOpen={isLinkModalOpen}
                            onClose={() => {
                                setIsLinkModalOpen(false);
                                setLinkCopied(false);
                            }}
                            checkoutLink={checkoutLink}
                            linkCopied={linkCopied}
                            onCopy={handleLinkCopy}
                        />
                    </form>
                    {/* Right: Preview slider */}
                    <div className={['w-full', previewVisible ? 'block' : 'hidden', 'lg:block'].join(' ',)}>
                        {/* Event Summary — same UI as QuotePreview, two items per row */}
                        <div className={`w-full bg-white rounded-2xl mb-4 p-4 ${Number(openIndex) === 0 ? 'hidden' : 'block'}`}>
                            <div className='flex items-center gap-1 mb-2'>
                                <span className="h-3 w-1 bg-orange-500 rounded" />
                                <h2 className='text-xs font-semibold text-[#282828]'>Event Summary</h2>
                            </div>
                            <div className='ml-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1'>
                                <p className='text-sm'>
                                    <span className='text-[#282828] font-[400] text-xs'>Event Date:</span>{' '}
                                    <span className='text-gray-500 text-xs'>
                                        {formattedDate(eventDetails.eventDate)}
                                        {/* {eventDetails.eventDate ? eventDetails.eventDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} */}
                                    </span>
                                </p>
                                <p className='text-sm'>
                                    <span className='text-[#282828] font-[400] text-xs'>No of guests:</span>{' '}
                                    <span className='text-gray-500 text-xs'>{eventDetails.noOfGuests || '—'}</span>
                                </p>
                                <p className='text-sm'>
                                    <span className='text-[#282828] font-[400] text-xs'>Duration:</span>{' '}
                                    <span className='text-gray-500 text-xs'>
                                        {eventDetails.startTime && eventDetails.endTime
                                            ? (() => {
                                                const mins = diffMinutesAllowNextDay(eventDetails.startTime, eventDetails.endTime);
                                                const h = Math.floor(mins / 60);
                                                const m = mins % 60;
                                                return m > 0 ? `${h}h ${m}m` : `${h} hr${h !== 1 ? 's' : ''}`;
                                            })()
                                            : '—'}
                                    </span>
                                </p>
                                <p className='text-sm'>
                                    <span className='text-[#282828] font-[400] text-xs'>Total drinks:</span>{' '}
                                    <span className='text-gray-500 text-xs'>
                                        {eventDetails.estDrinks != null && eventDetails.estDrinks !== '' ? eventDetails.estDrinks : '—'}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <ReviewSlider
                            items={slides}
                            index={pageIndex}
                            onIndexChange={handlePageChange}
                            className='md:sticky md:top-4'
                        />
                        {/* Quote Actions — Generate Link & Download at bottom of page (visible from laptop up) */}
                        {/* <div className='hidden lg:flex flex-row flex-wrap justify-center sm:justify-end gap-2 sm:gap-3 md:gap-4 mt-6 pt-4 border-t border-gray-200'>
                            <Button
                                onClick={() => handleLinkModal()}
                                type='button'
                                disabled={isLinkLoading}
                                classes='border border-black text-black flex items-center justify-center gap-2 px-4 py-2'
                            >
                                <img src={shareIcon} className='brightness-0 h-4 w-4' alt='Share' />
                                <span>{isLinkLoading ? 'Generating Link...' : 'Generate Link'}</span>
                            </Button>
                            {quote && (
                                <Button
                                    onClick={() => downloadQuotePdf(quote)}
                                    type='button'
                                    disabled={downloadLoading}
                                    classes='bg-black text-white flex items-center justify-center gap-2 px-4 py-2'
                                >
                                    <img src={downloadIcon} className='h-4 w-4' alt='Download' />
                                    <span>{downloadLoading ? 'Downloading...' : 'Download'}</span>
                                </Button>
                            )}
                        </div> */}
                    </div>
                </div>
            </div>
        </div >
    );
};

export default QuoteBuilder;
